// Pointer / drag / selection interaction layer for the game board,
// extracted from GameBoard.jsx. Owns the full mouse pipeline: mousedown
// hit routing (cards, piles, dice, tracker buttons, marquee start),
// drag-reach computation and movement/region/site gating for fresh
// plays, mousemove hover + drag, mouseup commit, double-click, pings,
// context menu, and the window-level key/blur handlers that support it.
//
// Host contract: the GameBoard surface (scene, meshes, physics, state,
// selection sets, drag fields) plus the commit primitives
// (_finalizeFreshPlayCommit, _moveCardToCell, _applyTap, sendCardToPile).
// Game-rule reachability/legality math lives in engine/core; this layer
// only orchestrates it against meshes and input events.

import { UI, playUI } from '../../../utils/arena/uiSounds';
import { CARD_THICKNESS, TOKEN_DRAG_Y, TOKEN_REST_Y } from '../../../utils/game/cardMesh';
import { setHoveredCard as setZoomBusHoveredCard } from '../../../utils/game/cardZoomBus';
import { liftDie, settleDie } from '../../../utils/game/diceMesh';
import { moveDiceIntent } from '../../../utils/game/net/diceIntents';
import { isDisabled, ownerElementAllowsCast } from '../../../utils/game/engine/automation';
import { bannedSiteCellsFor, extendedSpawnCellsFor } from './boardLegality';
import { harbingerExtraSpawnCells, dragonPortalMarkCells } from '../../../utils/game/engine/automation/ops/cards/harbinger';
import { collectThresholdWaivers, thresholdWaiverCellsFor } from '../../../utils/game/engine/automation/ops/thresholdWaiver';
import { AURA_SHAPE, anchorCoveredCells, countsAsMinion, countsAsUnit, aura4AnchorWorldPoint, currentRegion, getAffordability, getAuraShape, getLegalSitePlacement, hasAuraFootprint, hasBurrowing, hasSubmerge, hasSummoningSickness, isEdgeBlockedForUnit, isMonumentCard, legalAura2Anchors, legalAura4Anchors, legalAuraAtopSiteAnchors, legalAuraAtopVoidAnchors, ownedSiteCells, reachableLocationsInSteps, resolveDropRegion, spawnableCellsForMinion, spawnableCellsForSubsurfacePlay, subsurfaceRegionForPlay, effectiveMovementBonus } from '../../../utils/game/engine/core';
import { computeGridPositions, getCellNumber } from '../../../utils/game/grid';
import { matchAction } from '../../../utils/game/keybindings';
import { moveKinematicBody } from '../../../utils/game/physicsWorld';
import { playSound } from '../../../utils/game/sounds';
import { getGameGrid, setTrackerPosition } from '../../../utils/game/spawnConfig';
import { castPositionFor, isCastPositionLegal, legalCastCells, legalOwnerSummonCells, ownerSummonRestriction } from '../../../utils/game/engine/core/castRestrictions';
import { routeBoardClickToFocus } from './cellFocus';

// Legal summon cells for an owner-locked minion ("Only It Can Cast") whose
// OWNER restricts where it may be summoned, relative to the owner unit
// (Omphalos: "Minions it casts must be summoned here" → the owner's own cell).
// Returns the cell set, or null when there's no such restriction.
// Keys present in both sets, as a new Set.
function intersectCells(a, b) {
  const out = new Set();
  for (const key of a) if (b.has(key)) out.add(key);
  return out;
}

function ownerSummonCellsFor(host, card) {
  if (!card?._castableOnlyBy || !countsAsMinion(card)) return null;
  const ownerMesh = host.meshes.get(card._castableOnlyBy);
  const owner = ownerMesh?.userData?.cardInstance;
  if (!owner) return null;
  const cell = host._cellAtWorld(ownerMesh.position.x, ownerMesh.position.z);
  if (!cell) return null;
  return legalOwnerSummonCells(owner.name, cell);
}
import { getTotalPositions, indexToRowPosition } from '../../../utils/game/trackerConfig';
import { toast } from 'sonner';
import { isConnected } from '../../../utils/game/socketClient';
import * as THREE from 'three';
import { isDevBuild } from '../../../utils/devMode';
import { cardLogLabel } from './logbookChat';
import { k, t } from '../../../localization/t';
import { msg } from '../../../utils/game/logMessage';

// English label for a positional cast restriction code (castPositionFor).
const CAST_POSITION_LABEL = {
  back_row: k('back row'),
  front_row: k('front row'),
  outer_column: k('outer column'),
  outer_row: k('outer row'),
  corner: k('corner'),
  edge: k('edge'),
};

export function handleMouseDown(host, event) {
  // Any board interaction releases the logbook chat input so keyboard
  // hotkeys (ping with Q, etc.) reach the board instead of being typed
  // into the still-focused textbox. mousedown fires for every button,
  // so this covers left / right / middle clicks. We blur explicitly
  // because the handlers below call preventDefault(), which suppresses
  // the browser's native click-to-blur of the contenteditable. Escape
  // already blurs via the input's own onKeyDown.
  host.chatInputRef.current?.blur();

  // Remember where the right button went down so mouseup can tell a
  // stationary right-click (open context menu) from a camera pan drag.
  if (event.button === 2) {
    host._rightDownPos = { x: event.clientX, y: event.clientY };
  }

  // Ping mode swallows left clicks: they only fire pings, never pick
  // up cards or start a marquee. Camera controls (right/middle click)
  // still work normally.
  if (host.pingMode && event.button === 0) {
    host.firePingAtMouse(event);
    event.preventDefault();
    return;
  }
  // Defend / intercept selection: a left-click on the board either
  //   • toggles an on-cell eligible unit (or deselects a chosen one), or
  //   • starts a drag of an off-cell eligible defender (moveIds).
  // Everything else on the board is blocked until the prompt resolves.
  // Right/middle clicks fall through so the camera still works.
  if (host._combatSelect && event.button === 0) {
    const dHits = host.scene.raycastObjects(event, host.getInteractableMeshes());
    const dHit = dHits.length ? host.findHitObject(dHits[0].object) : null;
    // Cell fan works DURING combat selection: square-mode clicks open /
    // switch the fan (crowded squares are where defender picking needs it)
    // and an open fan still collapses on outside clicks. A 'member' result
    // falls through so clicking a fanned candidate toggles it normally.
    if (host.squareMode || host.state.focusedCell) {
      if (routeBoardClickToFocus(host, event, dHit) === true) {
        event.preventDefault();
        return;
      }
    }
    const ci = dHit?.userData?.cardInstance;
    const sel = host._combatSelect;
    if (isDevBuild()) {
      console.debug('[combat] mousedown during select', {
        hit: ci ? `${ci.name || ci.id}` : null,
        onCell: ci ? sel.onCellIds.has(ci.id) : false,
        draggable: ci ? sel.moveIds.has(ci.id) : false,
        chosen: ci ? sel.chosen.has(ci.id) : false,
      });
    }
    if (ci && (sel.onCellIds.has(ci.id) || sel.chosen.has(ci.id) || ci.id === sel.targetId)) {
      // Don't toggle on press: start a drag and decide on RELEASE, so
      // on-cell units can be dragged around (rearranged) without being
      // chosen as defenders. A release without movement = the old click.
      const mesh = host.meshes.get(ci.id);
      if (mesh) {
        host.dragging = {
          combatDefenderCard: ci, mesh, fromCell: true,
          originX: mesh.position.x, originZ: mesh.position.z,
          downX: event.clientX, downY: event.clientY,
        };
        ci.interactionOrder = host._nextInteractionOrder();
        if (!host.ownership.is(ci.id, 'local')) host.ownership.setLocal(ci.id);
        host._presenceClaim(ci.id, ci.interactionOrder);
        playSound('cardPickup');
      } else {
        host._toggleCombatSelection(ci.id);
      }
    } else if (ci && sel.moveIds.has(ci.id)) {
      const mesh = host.meshes.get(ci.id);
      if (mesh) {
        host.dragging = {
          combatDefenderCard: ci, mesh,
          originX: mesh.position.x, originZ: mesh.position.z,
          // Screen-space press point: lets the release handler tell a
          // plain click (choose the defender) from a real drag.
          pressX: event.clientX, pressY: event.clientY,
        };
        // Picked up last = stacks on top, same rule as every other drag.
        ci.interactionOrder = host._nextInteractionOrder();
        // Claim it exactly like a normal drag so the SHARED per-frame stream
        // loop (stackPhysics) broadcasts the live pose. The opponent sees the
        // defender being dragged in real time. No separate defend-sync path.
        if (!host.ownership.is(ci.id, 'local')) host.ownership.setLocal(ci.id);
        host._presenceClaim(ci.id, ci.interactionOrder);
        playSound('cardPickup');
        if (isDevBuild()) console.debug('[combat] defender drag started', ci.name || ci.id);
      }
    }
    event.preventDefault();
    return;
  }
  // Spectators can orbit the camera (right-click / middle-click) but can't interact with cards
  if (host.props.isSpectating) {
    if (event.button !== 0) return; // allow non-left-click through for camera
    // Replay: remember the press so mouseup can tell a plain click
    // (inspect the card under the cursor) from camera travel.
    if (host.props.replayDriver) host._replayDownPos = { x: event.clientX, y: event.clientY };
    return;
  }
  if (event.button !== 0) return;
  host.closeAllMenus();

  // v1.88. Summon-placement queue takes priority over every other
  // click handler. A click on a halo'd cell spawns one token + pops
  // the queue; off-halo clicks are silently swallowed.
  if (host.state.pendingSummonPlacement) {
    const handled = host._tryResolveSummonPlacement(event);
    if (handled) { event.preventDefault(); return; }
    event.preventDefault();
    return;
  }

  // Two-step location targeting (v1.13). Clicks anywhere on the
  // board are reserved for picking a cell. A click on a valid cell
  // fires the op and clears the pending state; a click outside the
  // valid set is silently ignored (player can retry or hit ESC).
  // Server-authoritative cell pick (P6 cutover) takes priority over the local
  // cell-target path. A click on a server cell sends chooseCellIntent.
  if (host.state.pendingServerChoice?.kind === 'cell') {
    host._tryResolveServerCellChoice(event);
    // Consume the click either way (mid-pick): a click off the legal set just
    // keeps the prompt up, like the local cell-pick.
    event.preventDefault();
    return;
  }
  if (host.state.pendingCellTarget) {
    const handled = host._tryResolveCellTarget(event);
    if (handled) {
      event.preventDefault();
      return;
    }
    // Don't fall through. We don't want to start a drag / marquee /
    // ring menu while the player is mid-pick.
    event.preventDefault();
    return;
  }

  if (host.state.isEditingGrid && host.gridEditor) {
    const point = host.scene.raycastTablePoint(event);
    if (point) {
      const handles = host.gridRenderer?.getHandleMeshes() || [];
      let handleMesh = null;
      if (handles.length > 0) {
        const hits = host.scene.raycastObjects(event, handles);
        if (hits && hits.length > 0) handleMesh = hits[0].object;
      }
      if (host.gridEditor.pointerDown(point, handleMesh)) return;
    }
  }

  if (host.state.isEditingUiInversion && host.uiInversionEditor) {
    const point = host.scene.raycastTablePoint(event);
    if (point) {
      const uv = host.scene.worldToUv(point);
      const handles = host.uiInversionRenderer?.getHandleMeshes() || [];
      let handleMesh = null;
      if (handles.length > 0) {
        const hits = host.scene.raycastObjects(event, handles);
        if (hits && hits.length > 0) handleMesh = hits[0].object;
      }
      if (host.uiInversionEditor.pointerDown(uv, handleMesh)) {
        host.forceUpdate(); // refresh popover (active region, mode)
        event.preventDefault();
        event.stopPropagation();
        return;
      }
    }
  }

  if (host.state.isEditingComponents && host.playmatComponentEditor) {
    const point = host.scene.raycastTablePoint(event);
    if (point) {
      const pickables = host.playmatComponentRenderer?.getPickableMeshes() || [];
      let picked = null;
      if (pickables.length > 0) {
        const hits = host.scene.raycastObjects(event, pickables);
        if (hits && hits.length > 0) picked = hits[0].object;
      }
      if (host.playmatComponentEditor.pointerDown(point, picked)) {
        host.forceUpdate();
        event.preventDefault();
        event.stopPropagation();
        return;
      }
    }
  }

  if (host.state.isPlacingSpawns && host.state.activeSpawnKey) {
    const point = host.scene.raycastTablePoint(event);
    if (point) {
      const key = host.state.activeSpawnKey;
      const newConfig = { ...host.state.spawnConfig, [key]: { x: Math.round(point.x * 10) / 10, z: Math.round(point.z * 10) / 10 } };
      host.setState({ spawnConfig: newConfig, activeSpawnKey: null });
      // spawn config is hardcoded. No persistence
      host.updateSpawnMarkers(newConfig);
    }
    return;
  }

  if (host.state.isPlacingSpawns && host.state.trackerEditing) {
    const point = host.scene.raycastTablePoint(event);
    if (!point) return;
    const { trackerKey, player, flatIndex } = host.state.trackerEditing;
    const { row, index } = indexToRowPosition(trackerKey, flatIndex);
    const roundedPoint = { x: Math.round(point.x * 10) / 10, z: Math.round(point.z * 10) / 10 };

    const newConfig = { ...host.state.spawnConfig };
    setTrackerPosition(newConfig, player, trackerKey, row, index, roundedPoint);

    host.addTrackerPreview(roundedPoint.x, roundedPoint.z);

    const total = getTotalPositions(trackerKey);
    const nextIndex = flatIndex + 1;
    if (nextIndex >= total) {
      host.setState({ spawnConfig: newConfig, trackerEditing: null }, () => {
        host.createTrackerTokens();
        host.createTrackerButtons();
      });
      host.clearTrackerPreviews();
      host.hideTrackerCursorPreview();
    } else {
      host.setState({ spawnConfig: newConfig, trackerEditing: { trackerKey, player, flatIndex: nextIndex } });
    }
    // spawn config is hardcoded. No persistence
    return;
  }

  const hits = host.scene.raycastObjects(event, host.getInteractableMeshes());

  if (hits.length === 0) {
    // An in-flight equip-pending selection cancels on empty-board
    // click. Pre-empts the marquee path below so the user can drop
    // the pending state without starting a fresh selection.
    if (host.state.equipPending.size > 0) {
      host.clearEquipPending();
      event.preventDefault();
      return;
    }
    // Empty board click. Before starting a fresh marquee, check
    // whether the click landed INSIDE the persistent hug outline of
    // the current selection. If it did, the user is picking up
    // the whole selection from a gap between cards, not starting a
    // new selection.
    if (host.isPointInsideHugRect(event.clientX, event.clientY) && host.selectedCardIds.size > 0) {
      const point = host.scene.raycastTablePoint(event);
      if (point) {
        host.startCardDrag({
          primaryMesh: null,
          dragIds: new Set(host.collectHighlightedCardIds()),
          anchorX: point.x,
          anchorZ: point.z,
        });
        event.preventDefault();
        return;
      }
    }

    // Cell → fan → act (opt-in): a SHIFT-click on the bare table resolves
    // to a cell and fans an occupied one open; any click collapses an open
    // fan. Runs after the hug-rect group pickup so multi-select drags keep
    // working over occupied cells, and before the marquee so neither a
    // fan gesture nor an exit click starts a stray marquee.
    if (routeBoardClickToFocus(host, event, null) === true) {
      event.preventDefault();
      return;
    }

    // Not inside the hug zone. Fresh marquee drag-select. Clear
    // any existing selection first. Marquee state lives on `this`
    // (not in React state) so mouse-move can render at 60fps
    // without triggering re-renders.
    host.selectedCardIds.clear();
    host.updateSelectionHighlights();
    host.marquee = {
      originX: event.clientX,
      originY: event.clientY,
      currentX: event.clientX,
      currentY: event.clientY,
    };
    host.renderMarqueeOverlay();
    event.preventDefault();
    return;
  }

  const hit = host.findHitObject(hits[0].object);

  // Drag existing token
  if (hit?.userData.type === 'token') {
    host.dragging = {
      mesh: hit,
      tokenInstance: hit.userData.tokenInstance,
      offsetX: 0,
      offsetZ: 0,
    };
    hit.position.y = TOKEN_DRAG_Y;
    event.preventDefault();
    return;
  }

  // Drag dice
  if (hit?.userData.type === 'dice') {
    host.dragging = {
      mesh: hit,
      diceInstance: hit.userData.diceInstance,
      offsetX: 0,
      offsetZ: 0,
    };
    liftDie(hit);
    playSound('cardPickup');
    event.preventDefault();
    return;
  }

  // Tracker +/- button click
  if (hit?.userData.type === 'trackerButton') {
    const { action, player, trackerKey } = hit.userData;
    host._flashTrackerButton(hit);
    if (action === 'increment' || action === 'integrated') host.incrementTracker(player, trackerKey);
    else host.decrementTracker(player, trackerKey);
    event.preventDefault();
    return;
  }

  // Cemetery opens the pile browser. For Atlas/Spellbook we defer
  // the action to mouseup/mousemove: a quick click draws to hand,
  // while a drag spawns the card on the table and enters a drag.
  if (hit?.userData.type === 'pile') {
    const pile = hit.userData.pile;
    const isCemetery = pile.name?.startsWith('Cemetery');
    const isOwned = host.isPileLocallyOwned(pile);
    // Shift-click any non-empty Spellbook/Atlas: pop the scry picker
    // right at the cursor, skipping the full pile menu. Restricted
    // to the local player's piles. The opponent's library is hidden
    // information in Sorcery TCG.
    if (event.shiftKey && !isCemetery && !pile.infinite && pile.cards.length > 0 && isOwned) {
      host._suppressNextRootClick = true;
      host.setState({
        contextMenu: {
          x: event.clientX,
          y: event.clientY,
          type: 'pileScry',
          pile,
        },
      });
      event.preventDefault();
      return;
    }
    if (isCemetery) {
      // Cemetery is public information; either player may browse it.
      host.openPileSearch(pile);
      event.preventDefault();
    } else if ((pile.name === 'Spellbook' || pile.name === 'Atlas') && isOwned) {
      // Click-to-draw / drag-to-spawn from the library or atlas is
      // an owner-only action. Ignoring the click on the opponent's
      // pile leaves it as a no-op rather than silently drawing from
      // their hidden library.
      host._pendingPileClick = {
        pileId: pile.id,
        pileMesh: hit,
        x: event.clientX,
        y: event.clientY,
      };
      event.preventDefault();
    }
    return;
  }

  // Left-click on a revealed card in the opponent's hand: open the
  // card inspector. The mesh carries the broadcast snapshot on
  // userData, which is enough for `renderCardInspector` to do its
  // full lookup against the sorcery card database.
  if (hit?.userData.type === 'opponentHandFace') {
    host.openOpponentHandInspector(hit.userData.cardInstance);
    event.preventDefault();
    return;
  }

  if (hit?.userData.type === 'card') {
    const cardInstance = hit.userData.cardInstance;
    // Ctrl/Cmd-click: toggle this card in the equip-pending selection.
    // Only locally-controlled cards can be queued as equip sources
    // (you can't equip an opponent's card). Suppresses drag and the
    // shift-click ring menu.
    if (event.ctrlKey || event.metaKey) {
      host.toggleEquipPending(cardInstance);
      event.preventDefault();
      return;
    }
    // Spellcaster pick (v1.14) takes priority. Until the player
    // picks a caster, the spell can't resolve. Valid clicks send
    // the caster's id into handlePromptConfirm as the choice value.
    const casterPrompt = host.state.pendingPrompts?.find((p) => p.promptType === 'pick_caster');
    if (casterPrompt) {
      const validIds = host.getEquipTargetIds();
      if (validIds?.has(cardInstance.id)) {
        host.handlePromptConfirm(casterPrompt.id, cardInstance.id);
        event.preventDefault();
        return;
      }
      // Click off a valid caster. Keep prompt open, swallow click so
      // it doesn't start a drag / marquee.
      event.preventDefault();
      return;
    }

    // Prompt clicks scan EVERY card under the ray, not just the topmost hit:
    // stacked squares (avatar + minion + site share a cell) can put a
    // non-candidate mesh in front of the candidate at the click pixel, and a
    // prompt click must land on the card the player aimed at regardless.
    const hitCards = [];
    const seenHitIds = new Set();
    for (const h of hits) {
      const obj = host.findHitObject(h.object);
      const ci = obj?.userData?.type === 'card' ? obj.userData.cardInstance : null;
      if (ci && !seenHitIds.has(ci.id)) {
        seenHitIds.add(ci.id);
        hitCards.push(ci);
      }
    }
    // Server-authoritative automation choice (P6 cutover) takes priority over the
    // local target path. A click on a server candidate sends chooseIntent.
    if (host.state.pendingServerChoice) {
      for (const ci of hitCards) {
        if (host._tryResolveServerChoice(ci)) {
          event.preventDefault();
          return;
        }
      }
    }
    // Single-target effect waiting for a click takes priority. Its
    // halos already filter to valid targets, so any matching click
    // commits. Falls through if the clicked card isn't valid (e.g.
    // user clicked off a halo by mistake).
    if (host.state.pendingTarget) {
      for (const ci of hitCards) {
        if (host._tryResolveTarget(ci)) {
          event.preventDefault();
          return;
        }
      }
    }
    // A pending equip-source selection turns the next plain click on
    // any locally-owned card into the "attach" commit. The target is
    // allowed even if it's also in the pending set. That just means
    // the source skips itself (see commitEquipPending).
    if (host.state.equipPending.size > 0) {
      const committed = host.commitEquipPending(cardInstance);
      if (committed) {
        event.preventDefault();
        return;
      }
      // Target wasn't valid (opponent's card etc.). Fall through to
      // normal click handling.
    }
    // Shift-click: the status ring menu. Opens immediately. No drag
    // ambiguity to disambiguate on a modifier click. (Square-selection
    // mode moved to the held expand hotkey, so shift is the ring's again.)
    if (event.shiftKey) {
      playUI(UI.CLICK);
      host.setState({ ringMenu: { cardInstance, x: event.clientX, y: event.clientY } });
      event.preventDefault();
      return;
    }
    // Cell → fan → act, OPT-IN: shift-click enters square-selection mode
    // and fans the clicked square open (the shift-click status-ring
    // shortcut moved to alt-click above). A
    // plain click on a fanned member (or any card while no fan is open)
    // falls through to the classic interaction: drag candidate from the
    // first pixel, quick release opens the action menu (handleMouseUp's
    // pendingCommit path). A plain click outside an open fan collapses it.
    const focusRoute = routeBoardClickToFocus(host, event, hit);
    if (focusRoute === true) {
      event.preventDefault();
      return;
    }
    const dragIds = host.collectDragGroupForCard(cardInstance.id);
    host.beginCardDragCandidate({
      primaryMesh: hit,
      dragIds,
      anchorX: hit.position.x,
      anchorZ: hit.position.z,
      startClientX: event.clientX,
      startClientY: event.clientY,
    });
    event.preventDefault();
    return;
  }
}

/**
 * Pick up a single card, a selection, or a group.
 *
 * `primaryMesh` is the card the user clicked (or null when dragging
 * from an empty spot inside the hug rect). `dragIds` is the set of
 * cards that should travel together. `anchorX`/`anchorZ` is the
 * world-space point each member's offset is computed against. For
 * card-clicks, this is the primary's own position; for hug-drags,
 * it's the raycast table point under the cursor. Both cases converge
 * in handleMouseMove, which just adds (point + offset) per member.
 */
// v1.32b / v1.35. Precompute the legal-destination set for every
// Minion/Avatar member of the drag. The shape depends on whether
// the card has a place of origin on the board:
//
//   • Moving on the board. One-step movement reach from the source
//     cell+region. Stored as { kind: 'move', reach, fromRegion, fromCell }.
//   • Fresh play (summoned from hand or pile draw). Spawn cells,
//     i.e. the cells where the player may legally summon the card
//     given their controlled sites + Voidwalk. Stored as
//     { kind: 'spawn', cells }.
//
// The result is consumed by:
//   • the per-frame reach overlay (halos)
//   • the drop validator in handleMouseUp (move only. Spawns are
//     not drop-gated; the overlay is purely advisory there)
export function computeDragReach(host, members, freshPlayIds) {
  if (!members || members.length === 0) return null;
  // Manual (automations-off) session: no legality engine runs, so no reach /
  // spawn / placement halos exist. No green legal cells and no red "can't
  // play here" paint. Every drop lands where the player put it; downstream
  // drop handlers treat a missing reach entry as a rules-free reposition.
  if (host._automationsEnabled?.() === false) return null;
  const grid = getGameGrid(host.state.spawnConfig);
  if (!grid) return null;
  const { colPositions, rowPositions } = computeGridPositions(grid);
  const totalCols = colPositions.length - 1;
  const totalRows = rowPositions.length - 1;
  const getFullCard = host._getFullCard;

  const out = new Map();
  for (const m of members) {
    const card = m.cardInstance;
    if (!card) continue;
    // Key the spawn/placement engine off the card's OWN ownership, never
    // off activePlayerNum. In casual sandbox the camera doesn't flip on
    // Tab, so reading the active seat to find "the avatar" can resolve to
    // the wrong side if state has updated unevenly. card.rotated is the
    // authoritative controller (matches the engine compatibility rule in
    // feedback_solo_mode_compat).
    const controllerRotated = !!card.rotated;

    // v1.108. Sites being dragged from hand get the green legal-
    // cell halo. v1.110. Strictly mirror codex placement rules
    // (the highlight is the source of truth for "where can I drop"):
    //   • Mandatory mode (Avatar standing in void): the ONLY legal
    //     cell is under the Avatar. Per codex "may and must use
    //     their ability to play a site under them, ignoring normal
    //     site adjacency rules." First-turn play always lands here.
    //   • Otherwise: cardinal-adjacent void cells next to one of
    //     your controlled non-Rubble sites. The Avatar's cell is
    //     NEVER itself a legal target in non-mandatory mode. It
    //     already has a site under it (otherwise the Avatar would
    //     be in void → mandatory mode → opposite branch).
    if (card.isSite) {
      if (!freshPlayIds?.has(card.id)) continue; // no halo for moving a placed site
      // Exclude the dragging site itself so its current cursor cell
      // isn't read as "already has a site" by cellIsVoid, which would
      // strip the legal cell from the halo set and turn it red.
      // Also union in any cells banned by an aura (Salt the Earth) so
      // those cells fall out of the legal placement set.
      const bannedCells = bannedSiteCellsFor(host);
      const legal = getLegalSitePlacement(
        controllerRotated, host._meshesExcludingStagedDrag(), host._cellAtWorld, getFullCard, totalCols, totalRows,
        bannedCells,
      );
      const cells = new Set();
      if (legal.mandatory && legal.underAvatar) {
        cells.add(`${legal.underAvatar.col},${legal.underAvatar.row}`);
      } else {
        for (const key of legal.adjacent) cells.add(key);
      }
      // The Apex of Babel may ALSO be played atop your The Base of Babel
      // (same square, merging into the Tower). Light the Base's cell(s) on
      // top of the ordinary placement set: the Base's "may build" is an
      // option, so the Apex still plays as a plain site anywhere legal.
      if (card.name === 'The Apex of Babel') {
        for (const [, otherMesh] of host._meshesExcludingStagedDrag()) {
          const other = otherMesh?.userData?.cardInstance;
          if (other?.isSite && other.name === 'The Base of Babel'
              && !!other.rotated === controllerRotated) {
            const c = host._cellAtWorld(otherMesh.position.x, otherMesh.position.z);
            if (c) cells.add(`${c.col},${c.row}`);
          }
        }
      }
      out.set(card.id, { kind: 'spawn', cells });
      continue;
    }

    // Position-restricted casts (The Round Table → your back row; corner /
    // outer-column cards). The legal cells are the positional set; the halo
    // reds every other cell, and applyFreshPlayGating hard-blocks an illegal
    // drop (returns the card to hand). Perspective-relative via controllerRotated.
    if (freshPlayIds?.has(card.id)) {
      const restricted = legalCastCells(card.name, grid, controllerRotated);
      if (restricted) {
        // The Round Table is a Monument: "your back row" narrows to the
        // back-row cells you actually control a site on (never a void or
        // an enemy site). Intersect the pure positional set with your own
        // sited cells. Non-Monument positional cards keep the raw set.
        const cells = isMonumentCard(getFullCard(card.cardId))
          ? intersectCells(restricted, ownedSiteCells(host._meshesExcludingStagedDrag(), host._cellAtWorld, controllerRotated))
          : restricted;
        out.set(card.id, { kind: 'spawn', cells });
        continue;
      }
      // A Monument with no positional restriction still conjures only atop
      // one of your sites (codex "Casting Artifacts": a non-carriable
      // artifact's only option is the surface of one of your sites). Light
      // those cells; every void / enemy-site cell reds, and the commit gate
      // hard-blocks an off-site drop.
      if (isMonumentCard(getFullCard(card.cardId))) {
        out.set(card.id, {
          kind: 'spawn',
          cells: ownedSiteCells(host._meshesExcludingStagedDrag(), host._cellAtWorld, controllerRotated),
        });
        continue;
      }
      // Owner-relative summon restriction (Omphalos "summoned here"): an
      // owner-locked minion may only land on its owner's own cell. The halo
      // lights that single cell; every other cell reds.
      const ownerCells = ownerSummonCellsFor(host, card);
      if (ownerCells) {
        out.set(card.id, { kind: 'spawn', cells: ownerCells });
        continue;
      }
    }

    // Aura placement halos. The drag preview mirrors the site-
    // placement UX: every cell an anchor would cover renders as a
    // movement-reach halo, and the cursor's nearest anchor is the
    // one that snaps on commit. Per frame, the renderer flips
    // `data-aura-active` on the cells of the nearest anchor so the
    // player sees exactly which cells the aura would occupy if
    // dropped here. No bespoke dot/bar visualization. It's all the
    // same cell-quad projector that drives every other placement
    // highlight, so the geometry stays locked to the grid under any
    // camera angle.
    //
    // Three shapes, three anchor types:
    //   INTERSECTION_4: corner anchors → 4 cells around the corner
    //   ATOP_SITE:      one site → 1 cell (the site itself)
    //   INTERSECTION_2: site edge → 2 cells flanking the wall
    // An animated aura (type Minion that kept its `_auraShape`) gets the
    // same footprint halo, not the single-cell minion-move reach.
    if (hasAuraFootprint(card)) {
      const shape = card._auraShape || getAuraShape(card);
      let anchors = [];
      if (shape.shape === AURA_SHAPE.INTERSECTION_4) {
        for (const a of legalAura4Anchors(totalCols, totalRows)) {
          const p = aura4AnchorWorldPoint(grid, a.col, a.row);
          if (!p) continue;
          anchors.push({
            ...a,
            point: p,
            coveredCells: anchorCoveredCells(a, AURA_SHAPE.INTERSECTION_4),
          });
        }
      } else if (shape.shape === AURA_SHAPE.ATOP_SITE) {
        const legal = legalAuraAtopSiteAnchors(
          card, host._meshesExcludingStagedDrag(), controllerRotated, getFullCard, host._cellAtWorld,
        );
        for (const a of legal) {
          anchors.push({
            ...a,
            coveredCells: anchorCoveredCells(a, AURA_SHAPE.ATOP_SITE),
          });
        }
      } else if (shape.shape === AURA_SHAPE.INTERSECTION_2) {
        const legal = legalAura2Anchors(
          host._meshesExcludingStagedDrag(), controllerRotated, host._cellAtWorld, grid,
        );
        for (const a of legal) {
          anchors.push({
            ...a,
            coveredCells: anchorCoveredCells(a, AURA_SHAPE.INTERSECTION_2),
          });
        }
      } else if (shape.shape === AURA_SHAPE.ATOP_VOID) {
        const legal = legalAuraAtopVoidAnchors(
          host._meshesExcludingStagedDrag(), host._cellAtWorld, grid, totalCols, totalRows,
        );
        for (const a of legal) {
          anchors.push({
            ...a,
            coveredCells: anchorCoveredCells(a, AURA_SHAPE.ATOP_VOID),
          });
        }
      }

      // Union covered cells → reach set. Off-grid keys are dropped
      // so we don't register DOM halos for cells that don't exist.
      const cells = new Set();
      for (const a of anchors) {
        for (const key of a.coveredCells || []) {
          const [cStr, rStr] = key.split(',');
          const c = parseInt(cStr, 10);
          const r = parseInt(rStr, 10);
          if (c < 0 || r < 0 || c >= totalCols || r >= totalRows) continue;
          cells.add(key);
        }
      }
      out.set(card.id, { kind: 'aura', shape: shape.shape, anchors, cells });
      continue;
    }

    const isMinionLike = countsAsUnit(card);
    if (!isMinionLike) continue;

    if (freshPlayIds?.has(card.id)) {
      // Fresh play. Origin is the hand, not a cell. Use spawn rules
      // (controlled non-Rubble site, or anywhere with Voidwalk). In
      // subsurface view the player has declared subsurface intent,
      // so the halo collapses to cells whose subsurface region
      // matches the card's Burrowing / Submerge keywords. Cards
      // without either keyword yield an empty cell set, so no
      // halos render and the drop is hard-blocked downstream.
      const inSubsurfaceView = host.state.regionView === 'subsurface';
      // v1.111. Harbinger's marked squares are added to the spawn-
      // legality set for Minion candidates while the once-per-turn
      // discount remains unused (the codex couples the cell extension
      // and the (1)-less discount into the same "Once on your turn"
      // gate). Subsurface plays don't take the extension: subsurface
      // entry still requires a controlled site to land into.
      const controller = controllerRotated ? 'p2' : 'p1';
      const harbingerExtras = harbingerExtraSpawnCells(
        host.effectLedger.entries, controller, card.type, getFullCard(card.cardId),
      );
      // Aura-driven legality extension (Crusade / Jihad / Eclipse /
      // Summoning Sphere / Evil Presence): if any spawn-extension
      // source's filter matches this card, its covered cells join the
      // legal-spawn set. Auth reads the server-derived boardLegality
      // records; sandbox walks the local meshes.
      const auraExtras = extendedSpawnCellsFor(host, card, controller);
      const placementExtras = (harbingerExtras.size === 0 && auraExtras.size === 0)
        ? harbingerExtras
        : new Set([...harbingerExtras, ...auraExtras]);
      const cells = inSubsurfaceView
        ? spawnableCellsForSubsurfacePlay(
            card, controllerRotated, totalCols, totalRows,
            host.meshes, host._cellAtWorld, getFullCard,
            placementExtras,
          )
        : spawnableCellsForMinion(
            card, controllerRotated, totalCols, totalRows, host.meshes, host._cellAtWorld,
            placementExtras,
          );
      // v1.111. Flag the subset of legal cells that come from the
      // Harbinger marks so MovementReachOverlay paints them in
      // purple. Two cases:
      //   (a) Card is Minion + player CANNOT afford at the non-cell
      //       discount → restrict cells to ONLY the marks (every
      //       other cell would fail mana). All halos render purple.
      //   (b) Card is Minion + player CAN afford anywhere AND
      //       Harbinger's discount would still apply on a mark →
      //       keep the full legal set, paint only the marks purple
      //       to advertise "you'll pay less if you land here."
      const harbingerCells = new Set();
      if (countsAsMinion(card) && harbingerExtras.size > 0) {
        const fc = getFullCard(card.cardId);
        if (fc) {
          const resources = host.playerResources.snapshot(controller);
          const nonCellDelta = host.effectLedger.nonCellDiscountTotal(controller, fc);
          const nonCellAfford = getAffordability(fc, resources, { costAdjustment: nonCellDelta });
          const cellDelta = host.effectLedger.cellRestrictedDiscountTotal(controller, fc, null);
          if (!nonCellAfford.ok) {
            const filtered = new Set();
            for (const key of cells) {
              if (harbingerExtras.has(key)) filtered.add(key);
            }
            cells.clear();
            filtered.forEach((k) => cells.add(k));
            filtered.forEach((k) => harbingerCells.add(k));
          } else if (cellDelta < 0) {
            for (const key of cells) {
              if (harbingerExtras.has(key)) harbingerCells.add(key);
            }
          }
        }
      }
      // Dragonlord's Lair / Caelestis share the cell-discount mechanic
      // with Harbinger, so their marks land in harbingerCells above.
      // Split the dragon-portal marks back out so the overlay can paint
      // them teal (matching the portal) instead of Harbinger purple.
      const dragonCells = new Set();
      const townCells = new Set();
      if (harbingerCells.size > 0) {
        const dragonExtras = dragonPortalMarkCells(
          host.effectLedger.entries, controller, getFullCard(card.cardId),
        );
        for (const key of [...harbingerCells]) {
          if (dragonExtras.has(key)) {
            dragonCells.add(key);
            harbingerCells.delete(key);
          }
        }
      }
      for (const key of [...harbingerCells]) {
        const [col, row] = key.split(',').map(Number);
        if (host.effectLedger.cellRestrictedDiscountKind(controller, getFullCard(card.cardId), { col, row }) === 'town') {
          townCells.add(key);
          harbingerCells.delete(key);
        }
      }
      // Dragonlord's Lair: when the candidate's thresholds are unmet,
      // the cast is legal ONLY on cells carrying a matching
      // WAIVE_THRESHOLD source ("Dragons cast to this site require no
      // threshold"). canPlayFromHand already forgave the threshold
      // refusal because a waiver exists; this is the cell-level half
      // of that bargain.
      if (countsAsMinion(card)) {
        const fc = getFullCard(card.cardId);
        if (fc) {
          const resources = host.playerResources.snapshot(controller);
          const afford = getAffordability(fc, resources, {
            costAdjustment: host.effectLedger.costModifierTotal(controller, fc),
          });
          const thresholdBlocked = !afford.ok
            && afford.refusals.length > 0
            && afford.refusals.every((r) => r.kind === 'threshold');
          if (thresholdBlocked) {
            const waivers = collectThresholdWaivers(host.meshes, host._cellAtWorld, getFullCard);
            const waiverCells = thresholdWaiverCellsFor(waivers, fc);
            const filtered = new Set();
            for (const key of cells) {
              if (waiverCells.has(key)) filtered.add(key);
            }
            cells.clear();
            filtered.forEach((k) => cells.add(k));
          }
        }
      }
      out.set(card.id, { kind: 'spawn', cells, harbingerCells, dragonCells, townCells });
      continue;
    }

    // v1.37b. A tapped Minion/Avatar has already used its action
    // this turn. No legal cell-cross destinations → no reach entry,
    // so no green halos render and the red invalid-cell hover fires
    // on every other square (updateInvalidCellHoverOverlay). The drop
    // itself is never blocked: _applyMovementGating advisory-logs and
    // emits the intent, and the server commits it as a questionable
    // manual reposition.
    //
    // v1.54. Same shape for summoning sickness.
    if (card.tapped || hasSummoningSickness(card)) continue;

    // Reach must share its origin with the server, which measures from the
    // authoritative card.cell. A mesh whose visual pose drifted from its
    // cell (an uncommitted manual roll) otherwise computes halos the server
    // rejects forever (GH-72). World position is the sandbox fallback.
    const fromCell = card.cell || host._cellAtWorld(m.initialX ?? 0, m.initialZ ?? 0);
    if (!fromCell) continue;
    const fullCard = getFullCard(card.cardId);
    const fromRegion = currentRegion(card, fromCell, host.meshes, host._cellAtWorld, getFullCard);
    // v1.52. Step budget = 1 base + Movement +N bonus from printed
    // text. Phantom Steed (`_movementBonus: 2`) reaches 3 cells in
    // any direction it can legally step into; vanilla units stay
    // at the original one-step shape.
    const stepBudget = 1 + effectiveMovementBonus(card);
    // Wall blocker. Feeds INTERSECTION_2 aura blocks (Wall of Air,
    // Wall of Ice, etc.) into the reach engine. Diagonal Airborne
    // steps are unaffected (no edge to cross at a corner).
    const wallBlocker = (unit, fromC, toC) =>
      isEdgeBlockedForUnit(unit, fromC, toC, host.meshes, getFullCard);
    const reach = reachableLocationsInSteps(
      card, fullCard,
      { col: fromCell.col, row: fromCell.row, region: fromRegion },
      stepBudget,
      host.meshes, host._cellAtWorld, totalCols, totalRows, getFullCard,
      wallBlocker,
    );
    out.set(card.id, { kind: 'move', reach, fromRegion, fromCell });
  }
  return out;
}

export function startCardDrag(host, { primaryMesh, dragIds, anchorX, anchorZ, freshPlay = false }) {
  // Disabled cards can't move (v1.16b). Refuse the drag on the
  // primary card; group drags still proceed for the non-disabled
  // members but the disabled member is filtered out of `dragIds`
  // before pickup.
  if (primaryMesh && isDisabled(primaryMesh.userData.cardInstance)) {
    toast.message(t("{name} is disabled. Can't move.", { name: primaryMesh.userData.cardInstance.name || t('Card') }));
    return;
  }
  // v1.36. Sorcery "Move and Attack" rule: a Minion or Avatar that
  // has already acted this turn is tapped, so a cell-cross move is
  // likely illegal. The drag proceeds anyway: _applyMovementGating
  // advisory-logs the warning on drop and the server transcribes the
  // move as a questionable manual reposition. Never a block.
  //
  // If the click is on an unrelated card (not in selection, not in a
  // group), clicking it clears any leftover marquee selection.
  if (primaryMesh) {
    const primaryId = primaryMesh.userData.cardInstance.id;
    if (!host.selectedCardIds.has(primaryId) && !primaryMesh.userData.cardInstance.groupId) {
      if (host.selectedCardIds.size > 0) {
        host.selectedCardIds.clear();
        host.updateSelectionHighlights();
      }
    }
  }

  const baseDragY = host.scene.CARD_DRAG_Y;
  const dragIdSet = dragIds instanceof Set ? dragIds : new Set(dragIds);

  // Collect every member with its ORIGINAL world pose at pickup
  // time. Offsets and initialY are stored per-member and never
  // recomputed mid-drag, so two grouped + stacked cards keep their
  // original vertical separation for the entire drag. The group
  // travels as one rigid card while every body stays a normal
  // collider that scatters non-group stacks just like a single
  // dragged card would.
  const members = [];
  let lowestMember = null;
  for (const cardId of dragIdSet) {
    const mesh = host.meshes.get(cardId);
    if (!mesh) continue;
    const m = {
      mesh,
      cardInstance: mesh.userData.cardInstance,
      offsetX: mesh.position.x - anchorX,
      offsetZ: mesh.position.z - anchorZ,
      initialX: mesh.position.x,
      initialY: mesh.position.y,
      initialZ: mesh.position.z,
    };
    members.push(m);
    if (!lowestMember || m.initialY < lowestMember.initialY) lowestMember = m;
  }

  // Compute the initial lift delta that puts the lowest member
  // above the stack it sits on (minus its own group-mates, which
  // dragIdSet excludes). Every member is shifted by the SAME delta
  // so relative stacking is preserved.
  let liftDelta = 0;
  if (lowestMember) {
    const stackTop = host.findStackHeightAt(
      lowestMember.mesh.position.x,
      lowestMember.mesh.position.z,
      dragIdSet
    );
    const lowestTargetY = Math.max(baseDragY, stackTop + CARD_THICKNESS + 1);
    liftDelta = Math.max(0, lowestTargetY - lowestMember.initialY);
  }

  // Bump interactionOrder on every dragged card and claim ownership.
  // Bodies are already kinematic + sensor (no contact response) from
  // creation, so there's no body-state transition to do here. The
  // bumped order pushes the card to the top of any stack at its
  // drop XZ on the next frame's _applyComputedY rank pass.
  for (const m of members) {
    const cardId = m.cardInstance.id;
    m.cardInstance.interactionOrder = host._nextInteractionOrder();
    if (!host.ownership.is(cardId, 'local')) {
      host.ownership.setLocal(cardId);
    }
    host._presenceClaim(cardId, m.cardInstance.interactionOrder);
    const y = m.initialY + liftDelta;
    m.mesh.position.y = y;
    moveKinematicBody(m.mesh.userData.body, m.mesh.position.x, y, m.mesh.position.z);
  }

  // Seed freshPlayIds BEFORE computing reach so _computeDragReach
  // can dispatch each member into the right mode (spawn vs move).
  // Fresh-play callers (hand → table, pile draw) pass freshPlay:true;
  // every member of the drag is then treated as a summon. v1.35.
  const freshPlayIds = freshPlay ? new Set([...dragIdSet]) : null;

  // v1.112. Assign `host.dragging` FIRST (without `reach`), then
  // compute reach. _computeDragReach -> _meshesExcludingStagedDrag
  // reads `host.dragging?.freshPlayIds` to exclude the staged site
  // mesh from the legality check; if reach were computed inside the
  // object literal, that read would still see the OLD dragging
  // (null on first drag), so the just-staged site would count as a
  // placed site and the four cells around the cursor's entry point
  // would light up as legal adjacency targets.
  host.dragging = {
    mesh: primaryMesh, // may be null for hug-drag
    cardInstance: primaryMesh?.userData?.cardInstance || null,
    offsetX: 0,
    offsetZ: 0,
    // Legacy field kept so unrelated consumers still see a number.
    dragY: (lowestMember ? lowestMember.initialY + liftDelta : baseDragY),
    members,
    // Cache the set of dragging ids once. HandleMouseMove reuses
    // it so findStackHeightAt skips every member in O(1) per hit.
    draggingIds: dragIdSet,
    // Monotonic shared lift applied to every member's initialY.
    // Only grows during the drag (never drops) so the group
    // doesn't yo-yo as the cursor crosses uneven terrain.
    liftDelta,
    freshPlayIds,
    // v1.32b / v1.35. Per-card legal-destination set. Entries are
    // tagged with kind: 'move' (one-step reach from source cell)
    // or kind: 'spawn' (legal summon cells, no source). Populated
    // immediately below.
    reach: null,
  };
  host.dragging.reach = host._computeDragReach(members, freshPlayIds);
  // Re-render once so the movement-reach overlay picks up the new
  // dragging.reach. Halos animate via the per-frame projector after.
  host.forceUpdate();
  // Document-level mousemove listener: needed because the hand DOM
  // (fixed at the bottom of the screen) swallows mousemove events
  // when the cursor is over it. Without this, we can't detect when
  // the user drags a card INTO the hand. The canvas never sees the
  // move. Tracks only the hand-handoff transition; the 3D drag logic
  // itself still runs off canvas mousemove events.
  document.addEventListener('mousemove', host.onDragCheckHandoff);
  playSound('cardPickup');
}

/**
 * Begin a drag *candidate* for a plain click on a board card. The card
 * follows the cursor immediately (handleMouseMove moves the members),
 * but none of the "this is a drag" side effects. Pickup sound, lift,
 * stack reorder, legal-move highlights. Fire yet. Those wait for
 * commitCardDrag once the cursor crosses the drag threshold. A release
 * before the threshold is a click (→ action menu), handled in
 * handleMouseUp. Mirrors startCardDrag's member collection without its
 * commit side effects.
 */
export function beginCardDragCandidate(host, { primaryMesh, dragIds, anchorX, anchorZ, startClientX, startClientY }) {
  if (primaryMesh && isDisabled(primaryMesh.userData.cardInstance)) {
    toast.message(t("{name} is disabled. Can't move.", { name: primaryMesh.userData.cardInstance.name || t('Card') }));
    return;
  }
  if (primaryMesh) {
    const primaryId = primaryMesh.userData.cardInstance.id;
    if (!host.selectedCardIds.has(primaryId) && !primaryMesh.userData.cardInstance.groupId) {
      if (host.selectedCardIds.size > 0) {
        host.selectedCardIds.clear();
        host.updateSelectionHighlights();
      }
    }
  }

  const dragIdSet = dragIds instanceof Set ? dragIds : new Set(dragIds);
  const members = [];
  for (const cardId of dragIdSet) {
    const mesh = host.meshes.get(cardId);
    if (!mesh) continue;
    members.push({
      mesh,
      cardInstance: mesh.userData.cardInstance,
      offsetX: mesh.position.x - anchorX,
      offsetZ: mesh.position.z - anchorZ,
      initialX: mesh.position.x,
      initialY: mesh.position.y,
      initialZ: mesh.position.z,
    });
  }
  if (members.length === 0) return;

  host.dragging = {
    mesh: primaryMesh,
    cardInstance: primaryMesh?.userData?.cardInstance || null,
    offsetX: 0,
    offsetZ: 0,
    dragY: primaryMesh ? primaryMesh.position.y : host.scene.CARD_DRAG_Y,
    members,
    draggingIds: dragIdSet,
    liftDelta: 0,
    freshPlayIds: null,
    reach: null,
    // Candidate state: handleMouseMove keeps the card flat against the
    // table and watches for the threshold; handleMouseUp opens the menu
    // if it's never crossed.
    pendingCommit: true,
    startClientX,
    startClientY,
  };
  document.addEventListener('mousemove', host.onDragCheckHandoff);
}

/**
 * Promote a drag candidate into a real drag. Runs the side effects
 * beginCardDragCandidate deferred: claim + reorder the cards (a click
 * must not reorder the stack, so this only happens once it's a genuine
 * drag), compute legal-move reach, repaint the overlay, and play the
 * pickup sound. The lift itself is applied by handleMouseMove's
 * per-frame liftDelta math on the same move that triggers the commit.
 */
export function commitCardDrag(host) {
  const drag = host.dragging;
  if (!drag || !drag.pendingCommit) return;
  drag.pendingCommit = false;

  // Dragging a card OUT of an open cell fan collapses the fan: the dragged
  // card belongs to the cursor now, and the remaining members glide home.
  // otherwise the dropped card would briefly fly back into the fan while
  // its move intent awaits the server confirm.
  if (host.state.focusedCell) host._closeCellFocus?.({ silent: true });

  for (const m of drag.members) {
    const cardId = m.cardInstance.id;
    m.cardInstance.interactionOrder = host._nextInteractionOrder();
    if (!host.ownership.is(cardId, 'local')) host.ownership.setLocal(cardId);
    host._presenceClaim(cardId, m.cardInstance.interactionOrder);
  }

  drag.reach = host._computeDragReach(drag.members, drag.freshPlayIds);
  host.forceUpdate();
  playSound('cardPickup');
}

export function onDragCheckHandoff(host, event) {
  if (!host.dragging || !host.dragging.members) return;
  if (host.dragging.diceInstance || host.dragging.tokenInstance) return;
  // Unit-hand drags are owned by UnitHandBadges (whole-hand drop zone +
  // reorder on release); skip the proactive hand handoff for them. Over
  // the PLAYER hand they still bounce back via handOffDragToHand's guard.
  if (host._unitHandDrag) return;
  if (!host.isPointOverHand(event.clientX, event.clientY)) return;
  const primary = host.dragging.members[0];
  const card = primary?.mesh?.userData?.cardInstance;
  if (card && host.ownership.is(card.id, 'local')) {
    document.removeEventListener('mousemove', host.onDragCheckHandoff);
    host.handOffDragToHand(card, event);
  }
}

export function handleMouseMove(host, event) {
  host.lastMouseEvent = event;

  if (host.state.isEditingGrid && host.gridEditor?.isActive()) {
    const point = host.scene.raycastTablePoint(event);
    if (point && host.gridEditor.pointerMove(point)) return;
  }

  if (host.state.isEditingUiInversion && host.uiInversionEditor?.isActive()) {
    const point = host.scene.raycastTablePoint(event);
    if (point) {
      const uv = host.scene.worldToUv(point);
      if (host.uiInversionEditor.pointerMove(uv)) return;
    }
  }

  if (host.state.isEditingComponents && host.playmatComponentEditor?.isActive()) {
    const point = host.scene.raycastTablePoint(event);
    if (point && host.playmatComponentEditor.pointerMove(point)) return;
  }

  // Pending pile click: if the cursor moves past the drag threshold,
  // commit to a drag. Spawn the top card of the pile onto the table
  // and begin a 3D drag. Otherwise, wait for mouseup to decide
  // whether this was just a click (→ draw to hand).
  if (host._pendingPileClick) {
    const dx = event.clientX - host._pendingPileClick.x;
    const dy = event.clientY - host._pendingPileClick.y;
    if (dx * dx + dy * dy > 25) {
      const { pileId, pileMesh } = host._pendingPileClick;
      host._pendingPileClick = null;
      // Drag-out from Spellbook / Atlas is also a draw and is
      // blocked while the start-phase prompt is pending.
      if (host._localDrawPhaseChoicePrompt()) {
        toast(t('Use Draw a Spell or Draw a Site first'));
        return;
      }
      // Offline the drag would be a local-only draw the server never hears
      // about (sendMatchCommand drops silently), reverted by the next diff.
      if (host._authZonesEnabled?.() && !isConnected()) {
        toast(t('Reconnecting. Try again in a moment.'));
        return;
      }
      // v1.104. Freeform pile drag-draw is unrestricted. Manual
      // control because draw automation isn't complete.
      host.drawCardToDrag(pileId, pileMesh);
      // Fall through so the card immediately tracks the cursor
      // on this same mousemove.
    } else {
      return;
    }
  }

  // Marquee drag: update the overlay rect and recompute the selection
  // from every card whose screen-projected position sits inside it.
  if (host.marquee) {
    host.marquee.currentX = event.clientX;
    host.marquee.currentY = event.clientY;
    host.renderMarqueeOverlay();

    const minX = Math.min(host.marquee.originX, host.marquee.currentX);
    const maxX = Math.max(host.marquee.originX, host.marquee.currentX);
    const minY = Math.min(host.marquee.originY, host.marquee.currentY);
    const maxY = Math.max(host.marquee.originY, host.marquee.currentY);

    const nextSelection = new Set();
    for (const [cardId, mesh] of host.meshes) {
      const screen = host.worldPositionToScreen(mesh.position);
      if (!screen) continue;
      if (screen.x >= minX && screen.x <= maxX && screen.y >= minY && screen.y <= maxY) {
        nextSelection.add(cardId);
      }
    }
    if (nextSelection.size !== host.selectedCardIds.size ||
        [...nextSelection].some((id) => !host.selectedCardIds.has(id))) {
      host.selectedCardIds = nextSelection;
      host.updateSelectionHighlights();
    }
    return;
  }

  if (host.dragging) {
    // Drag candidate (plain click on a board card): glide the card
    // flat against the table. No lift, no highlights. Until the
    // cursor crosses the threshold, then promote to a real drag and
    // fall through so this same frame applies the lift. A release
    // before the threshold is handled as a click in handleMouseUp.
    if (host.dragging.pendingCommit) {
      const dx = event.clientX - host.dragging.startClientX;
      const dy = event.clientY - host.dragging.startClientY;
      if (dx * dx + dy * dy <= 25) {
        const point = host.scene.raycastTablePoint(event);
        if (!point) return;
        for (const m of host.dragging.members) {
          const targetX = point.x + m.offsetX;
          const targetZ = point.z + m.offsetZ;
          m.mesh.position.x = targetX;
          m.mesh.position.z = targetZ;
          moveKinematicBody(m.mesh.userData.body, targetX, m.mesh.position.y, targetZ);
        }
        return;
      }
      commitCardDrag(host);
    }
    // Defender drag (defend prompt): follow the cursor on the table
    // plane; commit happens in handleMouseUp when dropped on the cell.
    if (host.dragging.combatDefenderCard) {
      const dp = host.scene.raycastTablePoint(event);
      if (dp) {
        host.dragging.mesh.position.x = dp.x;
        host.dragging.mesh.position.z = dp.z;
        if (host.dragging.mesh.userData?.body) {
          moveKinematicBody(host.dragging.mesh.userData.body, dp.x, host.dragging.mesh.position.y, dp.z);
        }
      }
      return;
    }
    // If the user drags a card from the table into the hand area,
    // seamlessly hand off to the hand-drag system. The card is
    // removed from the table and picked up into the hand without
    // the user releasing the mouse. Mirrors the commitHandDragToTable
    // flow in reverse.
    if (host.dragging.members && !host.dragging.diceInstance && !host.dragging.tokenInstance) {
      // Unit-hand drags are handled by UnitHandBadges' own document
      // listeners (whole-hand drop zone + reorder on release), so they
      // don't hand off here. Over the PLAYER hand an owner-locked card
      // still bounces back via handOffDragToHand's guard.
      if (!host._unitHandDrag && host.isPointOverHand(event.clientX, event.clientY)) {
        const primary = host.dragging.members[0];
        const card = primary?.mesh?.userData?.cardInstance;
        if (card && host.ownership.is(card.id, 'local')) {
          host.handOffDragToHand(card, event);
          return;
        }
      }
    }

    const point = host.scene.raycastTablePoint(event);
    if (!point) return;

    // Dice and token drags have no `members`. Handle them here
    // before the card-specific group-drag logic below.
    if (host.dragging.diceInstance || host.dragging.tokenInstance) {
      host.dragging.mesh.position.x = point.x + host.dragging.offsetX;
      host.dragging.mesh.position.z = point.z + host.dragging.offsetZ;
      return;
    }

    // Every member travels as part of a single rigid group. Single-
    // card drags are synthesised as a 1-member array so the loop
    // below handles both paths.
    const members = host.dragging.members;
    if (!members || members.length === 0) return;

    const draggingIds = host.dragging.draggingIds;

    // Find the member with the lowest initialY. That's the one
    // whose target position drives the group-wide stack-clear
    // math. Cached on the dragging record so we only walk the
    // member list once per drag, not per move event.
    let lowestMember = host.dragging._lowestMember;
    if (!lowestMember) {
      for (const m of members) {
        if (!lowestMember || m.initialY < lowestMember.initialY) lowestMember = m;
      }
      host.dragging._lowestMember = lowestMember;
    }

    // Compute where the lowest member WOULD land, then derive the
    // required shared lift to clear the stack at that point. The
    // result is a SINGLE group-wide liftDelta, so every member
    // keeps its original relative vertical offset and the group
    // moves as one frozen rigid object.
    const lowestTargetX = point.x + lowestMember.offsetX;
    const lowestTargetZ = point.z + lowestMember.offsetZ;
    const stackTop = host.findStackHeightAt(lowestTargetX, lowestTargetZ, draggingIds);
    const neededLowestY = Math.max(host.scene.CARD_DRAG_Y, stackTop + CARD_THICKNESS + 1);
    const neededLift = Math.max(0, neededLowestY - lowestMember.initialY);
    // Monotonic. Never drops during a drag.
    if (neededLift > host.dragging.liftDelta) host.dragging.liftDelta = neededLift;
    const liftDelta = host.dragging.liftDelta;

    // Apply the shared (dx, dy, dz) delta to every member. Each
    // member's Y is initialY + liftDelta, so two stacked cards
    // stay stacked at the same vertical separation they had when
    // the drag started.
    for (const m of members) {
      const targetX = point.x + m.offsetX;
      const targetZ = point.z + m.offsetZ;
      const targetY = m.initialY + liftDelta;
      m.mesh.position.x = targetX;
      m.mesh.position.y = targetY;
      m.mesh.position.z = targetZ;
      moveKinematicBody(m.mesh.userData.body, targetX, targetY, targetZ);
    }
    // Legacy field kept in sync for any consumer still reading it.
    host.dragging.dragY = lowestMember.initialY + liftDelta;

    // Cemetery + deck drop-zone highlights. Driven by the primary
    // member's current XZ so the indicator tracks the card the user
    // is aiming.
    const primaryPos = members[0]?.mesh?.position;
    if (primaryPos) {
      host.updateCemeteryDragHover(primaryPos.x, primaryPos.z);
      host.updateDeckDragHover(primaryPos.x, primaryPos.z);
    }
    return;
  }

  // Move tracker cursor preview with mouse
  if (host.state.isPlacingSpawns && host.state.trackerEditing && host.trackerCursorPreview) {
    const point = host.scene.raycastTablePoint(event);
    if (point) {
      host.trackerCursorPreview.position.x = Math.round(point.x * 10) / 10;
      host.trackerCursorPreview.position.z = Math.round(point.z * 10) / 10;
      host.trackerCursorPreview.visible = true;
    }
  }

  const hits = host.scene.raycastObjects(event, host.getInteractableMeshes());
  const hit = hits.length > 0 ? host.findHitObject(hits[0].object) : null;
  // During cell-pick mode (v1.18b) hover defers to the cell halos.
  // suppress card / pile / token hover so the cell stays the
  // primary input surface. Setting newHovered=null lets the existing
  // "hovered changed" branch below restore styles cleanly.
  // Suppress hover during cell-pick (v1.18b) AND while the
  // double-click action menu is open (v1.30c) so the menu's host
  // card isn't crowded by hover badges / damage / equipment peek.
  // A `noBackdrop` menu (the defend / intercept prompt) deliberately
  // leaves the board live so the player can inspect and drag the
  // highlighted units under it, so hover has to stay live too.
  const suppressHover = !!host.state.pendingCellTarget
    || (!!host.state.cardActionMenu && !host.state.cardActionMenu.noBackdrop);
  const newHovered = suppressHover
    ? null
    : (hit?.userData?.type === 'card' || hit?.userData?.type === 'pile' || hit?.userData?.type === 'token' || hit?.userData?.type === 'dice' || hit?.userData?.type === 'trackerButton' || hit?.userData?.type === 'opponentHandFace') ? hit : null;

  if (host.hoveredMesh !== newHovered) {

    // Remove highlight from old. Restore to the correct base state
    // rather than always resetting to black. If the opponent is
    // hovering the card or it's in our marquee selection, those
    // highlights take priority over the blank state.
    if (host.hoveredMesh?.material) {
      const oldCardId = host.hoveredMesh.userData?.cardInstance?.id;
      const isOpponentHover = oldCardId && oldCardId === host.opponentHoveredCardId;
      const isSelected = oldCardId && host.selectedCardIds.has(oldCardId);
      const restoreHex = isSelected ? 0x5a3f0a : isOpponentHover ? 0x0f2a55 : 0x000000;
      const mats = Array.isArray(host.hoveredMesh.material) ? host.hoveredMesh.material : [host.hoveredMesh.material];
      mats.forEach((m) => { if (m.emissive) m.emissive.setHex(restoreHex); });
    }
    // Hide stroke outline on old card, show on new
    if (host.hoveredMesh?.userData?.outline) host.hoveredMesh.userData.outline.visible = false;
    if (newHovered?.userData?.outline) newHovered.userData.outline.visible = true;

    // Add highlight to new. Emissive glow on every interactable
    // mesh, including the textured tracker click pads (which are now
    // array-materialled cylinders, not opacity-driven hit volumes).
    if (newHovered?.material) {
      const mats = Array.isArray(newHovered.material) ? newHovered.material : [newHovered.material];
      const hex = newHovered.userData?.type === 'trackerButton' ? 0x4a3a16 : 0x222222;
      mats.forEach((m) => { if (m.emissive) m.emissive.setHex(hex); });
    }
    host.hoveredMesh = newHovered;
    host.syncHoverStatusOverlay();
    // If Space was pressed/held before a card was hovered, the next
    // card hover opens the inspector.
    host.tryOpenInspectorFromArmed();
    // Hotspots match the regular-UI rules in index.css so the cursor
    // tip stays put when crossing into an interactive 3D object: the
    // pointer fingertip lives at (4,3), the default arrow tip at (4,2).
    host.canvasRef.current.style.cursor = newHovered
      ? "url('/cursors/pointer.png') 4 3, pointer"
      : "url('/cursors/default.png') 4 2, auto";

    // Broadcast to the opponent which card we're hovering so they
    // see a highlight on their board. Only cards are interesting.
    // piles, tokens, and buttons don't need remote feedback.
    const hoveredCardInstance = newHovered?.userData?.type === 'card'
      ? newHovered.userData.cardInstance
      : null;
    host._presenceHover(hoveredCardInstance?.id || null);
    host._setFocusedPreviewCard(hoveredCardInstance);
    setZoomBusHoveredCard(hoveredCardInstance);
  }

}

export function handleMouseUp(host, event) {
  // Right-button release: open the context menu only if the cursor
  // stayed put since right-down. A drag beyond the threshold was a
  // camera pan and must never pop a menu.
  if (event.button === 2) {
    const start = host._rightDownPos;
    host._rightDownPos = null;
    if (start && Math.hypot(event.clientX - start.x, event.clientY - start.y) <= 4) {
      maybeOpenContextMenu(host, event);
    }
    return;
  }

  if (host.props.isSpectating) {
    // Replay: a stationary left click on a board card opens the inspector.
    // Nothing can mutate (the intent paths are all gated); inspecting a
    // frozen moment is the point of a replay. Walk past non-card hits the
    // same way the context menu does, so an overlapping mesh can't swallow
    // the click.
    if (host.props.replayDriver && event.button === 0) {
      const start = host._replayDownPos;
      host._replayDownPos = null;
      if (start && Math.hypot(event.clientX - start.x, event.clientY - start.y) <= 4) {
        const hits = host.scene.raycastObjects(event, host.getInteractableMeshes());
        for (const raycastHit of hits) {
          const candidate = host.findHitObject(raycastHit.object);
          if (candidate?.userData?.type === 'card') {
            const cardId = candidate.userData.cardInstance?.cardId;
            if (cardId) host.handleOpenLogbookMention(cardId);
            break;
          }
          // A face-up card in a 3D hand fan (the opponent's, or in a
          // replay either seat's not-the-viewer hand).
          if (candidate?.userData?.type === 'opponentHandFace') {
            const cardId = candidate.userData.cardInstance?.cardId;
            if (cardId) host.handleOpenLogbookMention(cardId);
            break;
          }
        }
      }
    }
    return; // No other click actions for spectators
  }

  if (host.state.isEditingGrid && host.gridEditor?.isActive()) {
    if (host.gridEditor.pointerUp()) return;
  }

  if (host.state.isEditingUiInversion && host.uiInversionEditor?.isActive()) {
    if (host.uiInversionEditor.pointerUp()) {
      host.forceUpdate(); // popover region count / mode transition
      return;
    }
  }

  if (host.state.isEditingComponents && host.playmatComponentEditor?.isActive()) {
    if (host.playmatComponentEditor.pointerUp()) {
      host.forceUpdate();
      return;
    }
  }

  // Defender drag release: commit as a defender if dropped on the
  // contested square, otherwise snap back. Self-contained. Does not go
  // through the turn-gated move pipeline.
  if (host.dragging?.combatDefenderCard) {
    const {
      combatDefenderCard: card, mesh, originX, originZ,
      fromCell, downX, downY, pressX, pressY,
    } = host.dragging;
    host.dragging = null;
    const drop = host._cellAtWorld(mesh.position.x, mesh.position.z);
    const cc = host._combatSelect?.contestedCell;
    if (isDevBuild()) console.debug('[combat] defender drag released', { drop, contestedCell: cc });
    playSound('cardPlace');
    const setMeshPos = (x, z) => {
      mesh.position.x = x; mesh.position.z = z;
      if (mesh.userData?.body) moveKinematicBody(mesh.userData.body, x, mesh.position.y, z);
    };
    if (fromCell) {
      // On-cell / chosen / target unit. A release without movement is a
      // click: snap back and toggle (the old mousedown behaviour). A real
      // drag is a free reposition and never changes the selection.
      if (Math.hypot(event.clientX - downX, event.clientY - downY) <= 4) {
        setMeshPos(originX, originZ);
        host._toggleCombatSelection(card.id);
      }
      event.preventDefault();
      return;
    }
    let onContested = cc && drop && drop.col === cc.col && drop.row === cc.row;
    // A press-and-release without mouse travel is a plain CLICK on the
    // highlighted defender: choose it, auto-moving it onto the contested
    // square (same commit as dropping it there). Without this, a click
    // ended in a silent snap-back. The teal halo doesn't distinguish
    // click-to-toggle (on the square) from drag-to-move (off it), and QA
    // read that as "can't click Avatars / tokens to defend" (issue #47).
    if (!onContested && cc && pressX != null
        && Math.hypot(event.clientX - pressX, event.clientY - pressY) < 5) {
      const center = host._cellCenterWorld?.(cc.col, cc.row);
      if (center) {
        setMeshPos(center.x, center.z);
        onContested = true;
      }
    }
    if (onContested) {
      // Keep the mesh EXACTLY where the player dropped it. The codex
      // only requires occupying the attack's location, not its centre.
      // and pay the declaration tap NOW (codex Defend: "it may tap to
      // defend, moving to the attack's location", declared one unit at
      // a time). Deselect / Clear refunds the tap and snaps the unit
      // back (_toggleCombatSelection / _combatClearSelect). The host's
      // resolve-time tap is idempotent.
      host._combatSelect?.chosen?.add(card.id);
      host._combatSelect?.draggedOrigins?.set(card.id, { x: originX, z: originZ });
      host._setCombatSelectionTap(card.id, true);
      host._refreshCombatPrompt();
    } else {
      setMeshPos(originX, originZ); // not the contested square. Revert
    }
    event.preventDefault();
    return;
  }

  // Quick click on a pile (no drag): draw the top card to hand.
  // v1.104. Freeform; no per-turn draw cap.
  if (host._pendingPileClick) {
    const { pileId } = host._pendingPileClick;
    host._pendingPileClick = null;
    // While the mandatory start-phase draw is pending, route a click
    // on the matching pile through the prompt itself instead of the
    // freeform draw path. Clicking the Spellbook resolves the prompt
    // as if the Draw Spell button was pressed, Atlas as Draw Site.
    const drawPrompt = host._localDrawPhaseChoicePrompt();
    if (drawPrompt) {
      if (pileId === drawPrompt._spellbookPileId) {
        host.handlePromptConfirm(drawPrompt.id, 'spellbook');
      } else if (pileId === drawPrompt._atlasPileId) {
        host.handlePromptConfirm(drawPrompt.id, 'atlas');
      }
      return;
    }
    host.playerDrawFromPile(pileId);
    return;
  }

  // Finish a marquee selection: clear the transient rect and the
  // overlay DOM, leaving host.selectedCardIds populated for the
  // subsequent drag/right-click.
  if (host.marquee) {
    host.marquee = null;
    host.renderMarqueeOverlay();
    return;
  }

  if (!host.dragging) return;

  // Drag candidate released before the threshold: this was a click, not
  // a drag. Snap the (barely-moved) cards back to their anchor and open
  // the action menu. CommitCardDrag never ran, so there's nothing to
  // drop or reorder. Multiplayer gates the menu to locally-controlled
  // cards, matching the former double-click behaviour.
  if (host.dragging.pendingCommit) {
    const { members, mesh } = host.dragging;
    host.dragging = null;
    document.removeEventListener('mousemove', host.onDragCheckHandoff);
    for (const m of members) {
      m.mesh.position.x = m.initialX;
      m.mesh.position.y = m.initialY;
      m.mesh.position.z = m.initialZ;
      moveKinematicBody(m.mesh.userData.body, m.initialX, m.initialY, m.initialZ);
    }
    const cardInstance = mesh?.userData?.cardInstance;
    // A combat prompt is paramount. Block opening any card menu until
    // the player resolves it.
    const combatBlocking = host._activeCombatPrompt;
    if (cardInstance && !combatBlocking) {
      const isMultiplayer = host.state.connectionStatus !== 'offline';
      if (!isMultiplayer || host.isCardOnLocalSide(cardInstance)) {
        // Match the Zones button: it's a <button>, so opening it plays
        // the global UI.CLICK (its own UI.SELECT is a no-op). The menu
        // opens from a canvas click, which has no global handler, so
        // play UI.CLICK explicitly. Close plays UI.CANCEL (see
        // CardActionMenu backdrop) to match the Zones close.
        playUI(UI.CLICK);
        host._openCardActionMenu(cardInstance, event);
      }
    }
    return;
  }

  document.removeEventListener('mousemove', host.onDragCheckHandoff);

  const droppedMesh = host.dragging.mesh;

  // Token drop. Simple position update
  if (host.dragging.tokenInstance) {
    const token = host.dragging.tokenInstance;
    token.x = droppedMesh.position.x;
    token.z = droppedMesh.position.z;
    droppedMesh.position.y = TOKEN_REST_Y;
    host.dragging = null;
    return;
  }

  // Dice drop
  if (host.dragging.diceInstance) {
    const dice = host.dragging.diceInstance;
    settleDie(droppedMesh, host._diceContext());
    playSound('cardPlace');
    host._sendMatchIntent(moveDiceIntent(dice.id, dice.x, dice.z, dice.on));
    host.dragging = null;
    return;
  }

  // Clear any drop-zone highlights that were active during the drag.
  host.clearCemeteryDragHover();
  host.clearDeckDragHover();

  // Partition drop members into three buckets:
  //   1. cemeteryMembers. Dropped on the local cemetery pile
  //   2. deckMembers. Dropped on the local Spellbook or Atlas
  //                        (routed to the top of that deck)
  //   3. tableMembers. Everything else; falls back to physics
  //
  // Eligibility for the pile-routed buckets requires the card to be
  // owned by the local player (matches local rotation). Opponent-
  // owned cards always drop onto the table.
  const allMembers = host.dragging.members || [{ mesh: droppedMesh }];
  const localRotated = host.getActivePlayerRotated();
  const freshPlayIds = host.dragging.freshPlayIds;
  const cemeteryMembers = [];
  const deckMembers = [];   // entries: { member, deck: 'Spellbook' | 'Atlas' }
  const tableMembers = [];
  for (const m of allMembers) {
    const card = m.mesh?.userData?.cardInstance;
    const pos = m.mesh?.position;
    const ownedLocally = card && pos && !!card.rotated === localRotated;
    if (ownedLocally && host.isWithinLocalCemeteryDropZone(pos.x, pos.z)) {
      cemeteryMembers.push(m);
      continue;
    }
    const deckTarget = ownedLocally ? host.getLocalDeckDropTarget(pos.x, pos.z) : null;
    if (deckTarget) {
      deckMembers.push({ member: m, deck: deckTarget });
      continue;
    }
    tableMembers.push(m);
  }

  // Card drop. No per-card stack resolution needed. The next
  // frame's _applyComputedY computes a fresh rank for every card
  // from the global OBB-overlap relation, so dropped cards land at
  // the right Y purely from their bumped interactionOrder + their
  // new XZ position.
  const droppedGroupIds = new Set();
  for (const m of tableMembers) {
    const gid = m.mesh?.userData?.cardInstance?.groupId;
    if (gid && !droppedGroupIds.has(gid)) {
      droppedGroupIds.add(gid);
      host.groupSettleTimes.set(gid, performance.now());
    }
  }
  // Subsurface view hard-block runs FIRST so the rejected cards never
  // reach the advisory gating + commit block. Legal subsurface plays
  // get their card.subsurface stamped here too, which lets the
  // downstream placement prompt skip itself entirely.
  host._blockInvalidSubsurfacePlays(tableMembers, freshPlayIds);
  // Site-on-occupied-cell hard-block. Codex says two sites cannot
  // share a cell, ever. Like the subsurface rule above, this is one
  // of the few placement rules that's strictly enforced rather than
  // advisory: the offending site goes back to hand.
  host._blockInvalidSitePlacements(tableMembers, freshPlayIds);

  // v1.40. Fresh-play gating. Runs THREE checks per fresh play:
  // turn (controller must be the active player), cost (mana +
  // thresholds), and spawn cell (Minion/Avatar only). Any rejection
  // returns the card to its owner's hand BEFORE the commit block so
  // it never logs as "Played", mana isn't deducted, and site
  // play-effects never fire.
  host._applyFreshPlayGating(tableMembers, freshPlayIds);

  // Fresh-play commit: cards that were staged for a drag (from hand
  // or pile draw) have their "Played" log AND site play-effects
  // (mana bump, affinity recalc) deferred until now. Fire them only
  // for cards that actually ended up on the table. Anything dragged
  // to the cemetery or back to the hand has been routed elsewhere.
  if (freshPlayIds && freshPlayIds.size > 0) {
    for (const m of tableMembers) {
      const card = m.mesh?.userData?.cardInstance;
      if (card && freshPlayIds.has(card.id)) {
        host._finalizeFreshPlayCommit(card, m);
      }
    }
  }

  // v1.32d. Universal movement gating, advisory since S4: a drop
  // outside the precomputed reach set warns in the log and still
  // emits the move intent (the server transcribes it as a
  // questionable manual reposition). Dual-keyword units may
  // region-swap on arrival via resolveDropRegion.
  host._applyMovementGating(tableMembers, freshPlayIds);

  // v1.108. Site always-snapped invariant. A site should never sit
  // mid-cell or off-grid; whatever path put it where it is (fresh
  // play, drag-between-cells, snap-back from movement gating), the
  // final position must be the controller's bottom-center of the
  // cell beneath it. Runs AFTER movement gating so any snap-back
  // has already restored the original cell.
  for (const m of tableMembers) {
    const card = m.mesh?.userData?.cardInstance;
    if (card?.isSite) host._snapSiteToOwnerEdge(card, m.mesh);
  }

  // Aura always-snapped invariant. Same shape as sites. Auras
  // anchor at one of three shape-specific positions (4-cell corner,
  // single site, or site edge) and must never land mid-cell. Fires
  // on EVERY drag commit (fresh play OR intra-grid move) so the
  // snap behaves identically to site placement: wherever the aura
  // lands, it resolves to the nearest legal anchor.
  for (const m of tableMembers) {
    const card = m.mesh?.userData?.cardInstance;
    // Printed auras AND animated auras (type Minion that kept their
    // `_auraShape`) must re-snap so the footprint tracks where the unit
    // moved to.
    if (hasAuraFootprint(card)) {
      host._snapAuraToAnchor(card, m.mesh);
    }
  }

  // Intra-grid move logging + on_enter_location firing. Any card
  // that wasn't a fresh play and ended up in a different cell than
  // it started gets a "Moved X from [A] to [B]" line AND fires any
  // `on_enter_location` triggers installed on cards at the
  // destination (Druid's back-face site-granted damage, future
  // "enemy enters this site" effects). The fire runs BEFORE the
  // recompute below so we read the still-installed `_grantedTriggers`
  // from the previous pass. The recompute rebuilds them from
  // scratch so source-leave / scope-loss clears the grant for free.
  for (const m of tableMembers) {
    const card = m.mesh?.userData?.cardInstance;
    if (!card || freshPlayIds?.has(card.id)) continue;
    if (m.initialX == null || m.initialZ == null) continue;
    const fromCell = host._cellNumberAt(m.initialX, m.initialZ);
    const toCell = host._cellNumberAt(m.mesh.position.x, m.mesh.position.z);
    if (fromCell == null || toCell == null || fromCell === toCell) continue;
    const label = card.faceDown ? msg('a card face down') : (card.name || msg('a card'));
    host.logAction('Moved {card} from [{from}] to [{to}]', { card: label, from: fromCell, to: toCell });
    const fromCellObj = host._cellAtWorld(m.initialX, m.initialZ);
    const toCellObj = host._cellAtWorld(m.mesh.position.x, m.mesh.position.z);
    if (toCellObj) host._fireOnEnterLocationAt(card, toCellObj);
    // Wall triggers. Fire after on_enter so the unit is at its
    // destination when the wall's damage hits (matches the codex
    // ordering of "traverse, then resolve traverse effects").
    if (fromCellObj && toCellObj) host._fireOnEdgeTraverseAt(card, fromCellObj, toCellObj);
  }

  if (tableMembers.length > 0) {
    playSound('cardPlace');
    // Any drop on the table can change aura scope. A card might
    // have moved into or out of a "nearby" radius.
    host.recomputeStaticAuras();
  }
  host.dragging = null;
  // Re-render once so the movement-reach overlay clears.
  host.forceUpdate();

  // Route cemetery members AFTER clearing host.dragging. SendCardToPile
  // removes meshes from host.meshes and starts a fly-to-pile animation,
  // and we don't want any mid-flight reads of dragging.members to touch
  // cards that have already left the table.
  for (const m of cemeteryMembers) {
    const card = m.mesh?.userData?.cardInstance;
    if (!card) continue;
    // Staged fresh-play cards were never formally "played." If the
    // user dragged straight from hand/pile to the cemetery, suppress
    // the Played log and any site-play effects for this card.
    freshPlayIds?.delete(card.id);
    host.sendCardToPile(card, 'Cemetery');
  }

  // Route deck members. Cards dropped over the local Spellbook or
  // Atlas pile land on TOP of that deck. Same pattern as the cemetery
  // routing above: a staged fresh-play that never committed is
  // stripped from freshPlayIds so it doesn't log a spurious "Played".
  for (const { member, deck } of deckMembers) {
    const card = member.mesh?.userData?.cardInstance;
    if (!card) continue;
    freshPlayIds?.delete(card.id);
    host.sendCardToPile(card, deck, false, 'top');
  }
}

/**
 * Window-level mouseup: clean up marquee state that may have been left
 * behind when the drag left the canvas bounds. The canvas-level
 * handleMouseUp covers the common case; this is the backstop.
 */
export function handleWindowMouseUp(host, event) {
  if (host.marquee) {
    host.marquee = null;
    host.renderMarqueeOverlay();
  }
  // Fallback: if a 3D drag is still active and the release happened
  // off-canvas (e.g. over the hand div), route through the normal
  // drop path so the card drops back to the table cleanly.
  if (host.dragging && event.target !== host.canvasRef.current) {
    host.handleMouseUp(event);
  }
}

/**
 * Window-level Escape: abort an active marquee, and (on a second
 * press, or if there's no marquee) clear the current selection.
 * Input elements keep their own keydown handling. We bail if the
 * event originated from one.
 */
export function handleWindowKeyDown(host, event) {
  // App.jsx's document-level Escape handler runs first (document bubbles
  // before window) and calls stopPropagation when it claims the press, so
  // this window-level handler never sees Escapes the app already handled
  // (a just-closed codex/pile dialog, the pause-menu toggle, etc.). We do
  // NOT gate on event.defaultPrevented here. Main.jsx preventDefaults every
  // Escape in capture, so that flag is always set and is not a useful signal.
  const target = event.target;
  const tag = target?.tagName?.toLowerCase();
  if (tag === 'input' || tag === 'textarea' || target?.isContentEditable) return;

  if (event.key === 'Escape') {
    // Priority: ephemeral menus → floating dialogs → marquee/selection.
    // App.jsx defers to this handler whenever hasOpenOverlay() is truthy
    // so the app's escape menu doesn't double-fire on top.
    const s = host.state;
    if (s.showGameMenu || s.showDiceMenu || s.showAmbienceMenu ||
        s.showPlaymatPicker || s.showCodex ||
        s.contextMenu || s.ringMenu) {
      event.preventDefault();
      host.closeAllMenus();
      return;
    }
    if (s.showShortcutHelp) {
      event.preventDefault();
      host.setState({ showShortcutHelp: false });
      return;
    }
    if (s.showExitConfirm) {
      event.preventDefault();
      host.setState({ showExitConfirm: false });
      return;
    }
    if (host.state.equipPending.size > 0) {
      event.preventDefault();
      host.clearEquipPending();
      return;
    }
    if (host.state.pendingServerChoice) {
      // Optional ("may") prompt → decline (server clears it). A mandatory prompt
      // swallows ESC (the player must pick). Server effects can't be skipped.
      event.preventDefault();
      host._declineServerChoice();
      return;
    }
    if (host.state.pendingTarget) {
      event.preventDefault();
      host._cancelTargeting();
      return;
    }
    if (host.state.pendingCellTarget) {
      event.preventDefault();
      host._cancelCellTargeting();
      return;
    }
    if (host.state.pendingSummonPlacement) {
      event.preventDefault();
      host._cancelSummonPlacements();
      return;
    }
    if (host.state.cardActionMenu) {
      event.preventDefault();
      host._closeCardActionMenu();
      return;
    }
    if (host.marquee) {
      event.preventDefault();
      host.marquee = null;
      host.renderMarqueeOverlay();
      return;
    }
    if (host.selectedCardIds.size > 0) {
      event.preventDefault();
      host.selectedCardIds.clear();
      host.updateSelectionHighlights();
      return;
    }
  }
  // Remappable gameplay hotkeys are dispatched from app.jsx's
  // handleDocumentKeyDown → handleGameHotkey. Handling them here too
  // would double-fire every shortcut.
}

// Called from the hover pipeline whenever this.hoveredMesh changes,
// and from the hand-hover setter. If Space (card.inspect) was pressed
// without a card under the cursor, the next hover lands the inspector
// on that card.
export function tryOpenInspectorFromArmed(host) {
  if (host.state.inspectedCard) return;
  // Resolve a target card BEFORE consuming the armed flag. Otherwise
  // a hover transition that lands on a pile / token / empty table
  // burns the armed state and the next real card hover finds nothing
  // pending. Tap-armed users would never get the inspector to open.
  let card = null;
  let isTableMesh = false;
  let isOpponentHand = false;
  // Hand index wins over `hoveredMesh`. Same reason as in `card.inspect`
  // keydown: `hoveredMesh` goes stale when the cursor leaves the canvas
  // for the hand DOM overlay, while `hoveredHandIndex` is cleared on
  // mouseleave so it reflects the live cursor location.
  const { hoveredHandIndex } = host.state;
  const activeHand = host.getActiveHandCards();
  if (host._hoveredUnitSpell) {
    card = host._hoveredUnitSpell;
  } else if (hoveredHandIndex >= 0 && activeHand[hoveredHandIndex]) {
    card = activeHand[hoveredHandIndex];
  } else if (host.hoveredMesh?.userData?.type === 'card') {
    card = host.hoveredMesh.userData.cardInstance;
    isTableMesh = true;
  } else if (host.hoveredMesh?.userData?.type === 'opponentHandFace') {
    card = host.hoveredMesh.userData.cardInstance;
    isOpponentHand = true;
  }
  if (!card) return;
  if (!host.inspectorHold.consumePending()) return;
  if (isOpponentHand) {
    host.openOpponentHandInspector(card);
    return;
  }
  host.setState({ inspectedCard: card });
  host.scene?.setPaused(true);
  host._presenceHover(isTableMesh ? card.id : null);
}

// Releasing the card.inspect key (Space by default) closes the
// inspector only if it was held past the hold threshold. Quick taps
// leave it open (tap-toggle mode). Mirrors the gamepad LT release at
// handleGamepadButtonUp.
export function handleWindowKeyUp(host, event) {
  // Ping mode exits the moment the bound ping key is released,
  // regardless of any other modal state.
  if (host.pingMode && matchAction(event) === 'ping.fire') {
    host.pingMode = false;
    host.setState({ pingMode: false });
    return;
  }
  // Square-selection mode is held-key only: release exits (the open fan
  // itself stays. ESC / click outside collapses it).
  if (host.squareMode && matchAction(event) === 'board.expandSquare') {
    host.squareMode = false;
    return;
  }
  if (matchAction(event) !== 'card.inspect') return;
  // Tracker first: releasing Space also disarms a hover-less press.
  if (!host.inspectorHold.shouldCloseOnKeyUp() || !host.state.inspectedCard) return;
  event.preventDefault();
  host.setState({ inspectedCard: null, inspectorStack: [] });
  host.scene?.setPaused(false);
  host._presenceHover(host.hoveredMesh?.userData?.cardInstance?.id || null);
}

// Window blur (alt-tab, focus stolen by another app) while the ping
// key is held would otherwise strand us in ping mode. The keyup
// never fires because focus left first. Treat blur as a key-up for
// ping purposes.
export function handleWindowBlur(host) {
  if (host.pingMode) {
    host.pingMode = false;
    host.setState({ pingMode: false });
  }
  host.squareMode = false;
}

// Local-side fire of a ping at the current mouse position. Echoes
// immediately for snappy local feedback, then broadcasts the world
// coords + a timestamp so the receiver can drop stale pings.
// The ring is always gold (visibility over identity); the own/opponent
// distinction is carried by audio volume. Own pings play quieter so
// the player isn't deafened by their own clicks.
export function firePingAtMouse(host, event) {
  if (!host.pingLayer || !host.scene) return;
  if (!host.pingRateLimit.tryConsume()) return;
  const point = host.scene.raycastTablePoint(event);
  if (!point) return;
  host.pingLayer.fire({ x: point.x, y: point.y, z: point.z });
  try { playUI(UI.CLICK_SOFT, { volume: 0.45 }); } catch {}
  const pingId = (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `p-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  host.sync.firePing({ pingId, x: point.x, y: point.y, z: point.z, t: Date.now() });
}

/**
 * `G` hotkey handler. Toggles grouping for the current marquee
 * selection:
 *   - if every selected card is already in the same single group,
 *     dissolve that group
 *   - otherwise, if 2+ cards are selected, group them all into a
 *     fresh group (the assignGroup eviction logic absorbs any
 *     members that were in a different group)
 *   - selections of 0 or 1 cards do nothing
 *
 * Broadcasts the change via the existing group:set / group:clear
 * sync bridge methods so the opponent's client stays in lockstep.
 */
export function toggleGroupForSelection(host) {
  const ids = [...host.selectedCardIds];
  if (ids.length === 0) return;

  // Detect the "already a single cohesive group" case. When true,
  // hitting G should ungroup. Anything else (mixed groups, no
  // groups at all) should group into one.
  let sharedGid = null;
  let cohesive = false;
  if (ids.length >= 2) {
    const first = host.meshes.get(ids[0]);
    const gid = first?.userData?.cardInstance?.groupId || null;
    if (gid) {
      cohesive = ids.every((id) => {
        const mesh = host.meshes.get(id);
        return mesh?.userData?.cardInstance?.groupId === gid;
      });
      if (cohesive) sharedGid = gid;
    }
  }

  if (cohesive && sharedGid) {
    host.dissolveGroup(sharedGid);
    host._broadcastClearGroup(sharedGid);
    return;
  }

  if (ids.length >= 2) {
    const groupId = `group-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    host.assignGroup(groupId, ids);
    host._broadcastGroup(groupId, ids);
  }
}

// v1.30e. Gate every subsurface unit's drop. For each member that
// crossed a cell boundary AND has card.subsurface set, ask
// moveOutcome whether the destination supports the unit's region.
//   'preserve' (possibly with a region swap). Keep the move; if
//     the region swapped, update card.subsurface + broadcast.
//   'surface'. Destination has no matching subsurface; snap back
//     and toast. Per the user's rule, units don't auto-surface
//     during movement; they have to use the Surface menu item.
//   'die'. Void destination; snap back to avoid an unwanted
//     death from a fumbled drag.
// v1.40. Fresh-play gating. Three orthogonal checks per card,
// every rejection returns the card to the owner's hand via the
// staged-removal path so no site mana/affinity machinery reverses:
//
//   1. Turn check (every fresh play): the card's controller must
//      equal the active player. You cannot play during your
//      opponent's turn.
//   2. Cost check (every fresh play): controller must have enough
//      mana AND meet every elemental threshold. Sites bypass
//      (site-placement is mana-free; its own rules are separate
//      and not yet enforced). See engine/core/casting.js.
//   3. Spawn-cell check (Minion/Avatar fresh play only): the drop
//      cell must be in the precomputed spawn reach. Voidwalk
//      minions can spawn anywhere; vanilla minions only on
//      controlled non-Rubble sites.
//
// Mutates tableMembers AND freshPlayIds so the subsequent commit +
// move-log loops skip rejected cards.
// Hard-block fresh plays that violate subsurface-view placement rules.
// Runs only when the local player has the subsurface view active; in
// that mode they've already declared subsurface intent, so a fresh
// play must:
//   • carry the Burrowing or Submerge keyword (else rejected), AND
//   • land on a controlled cell whose subsurface region matches that
//     keyword (a Burrowing minion needs underground at the cell, a
//     Submerge minion needs underwater).
// Rejected cards return to hand via sendToHand; legal cards have
// their card.subsurface field stamped here so the surface-default
// placement prompt never opens.
export function blockInvalidSubsurfacePlays(host, tableMembers, freshPlayIds) {
  if (host.state.regionView !== 'subsurface') return;
  if (!freshPlayIds || freshPlayIds.size === 0) return;
  const getFullCard = host._getFullCard;
  for (let i = tableMembers.length - 1; i >= 0; i--) {
    const m = tableMembers[i];
    const card = m.mesh?.userData?.cardInstance;
    if (!card || !freshPlayIds.has(card.id)) continue;
    if (card.isSite) continue;
    if (!countsAsUnit(card)) continue;

    const burrows = hasBurrowing(card);
    const submerges = hasSubmerge(card);
    if (!burrows && !submerges) {
      toast.message(t("{name} can't be played in subsurface view without Burrowing or Submerge.", { name: card.name || t('Card') }));
      freshPlayIds.delete(card.id);
      tableMembers.splice(i, 1);
      host.sendToHand(card);
      continue;
    }

    const cell = host._cellAtWorld(m.mesh.position.x, m.mesh.position.z);
    const region = cell
      ? subsurfaceRegionForPlay(card, host.meshes, host._cellAtWorld, cell.col, cell.row, getFullCard)
      : null;
    if (!region) {
      const name = card.name || t('Card');
      toast.message(burrows && submerges
        ? t('{name} needs a land or water site you control to enter the subsurface.', { name })
        : (burrows
          ? t('{name} needs a land site you control to enter the subsurface.', { name })
          : t('{name} needs a water site you control to enter the subsurface.', { name })));
      freshPlayIds.delete(card.id);
      tableMembers.splice(i, 1);
      host.sendToHand(card);
      continue;
    }

    if (card.subsurface !== region) {
      card.subsurface = region;
      host._broadcastCardSubsurface?.(card.id, card.subsurface);
    }
  }
}

/**
 * Hard-block fresh-play site drops on cells that already hold a
 * site. The codex rule "a site goes onto a void (empty) cell" is
 * absolute. Two sites on one cell is impossible. So unlike the
 * mostly-advisory placement engine the user wants this enforced
 * strictly: reject the drop and return the card to hand, identical
 * to how an invalid subsurface play is rejected above.
 *
 * Applies in both surface and subsurface views. The rule doesn't
 * change based on which side of the playmat the player is looking
 * at. The red invalid-cell hover overlay already lights up over
 * occupied cells during the drag (legal cells are computed by
 * `getLegalSitePlacement`, which filters out non-void cells), so
 * the player gets visual feedback up front and this guard handles
 * the actual rejection on release.
 *
 * `_meshesExcludingStagedDrag` filters out every fresh-play mesh,
 * so the dragging site itself is invisible to the occupancy probe
 * (without that, every site placement would self-reject).
 */
export function blockInvalidSitePlacements(host, tableMembers, freshPlayIds) {
  if (!freshPlayIds || freshPlayIds.size === 0) return;
  const meshesWithoutDrag = host._meshesExcludingStagedDrag();

  for (let i = tableMembers.length - 1; i >= 0; i--) {
    const m = tableMembers[i];
    const card = m.mesh?.userData?.cardInstance;
    if (!card || !freshPlayIds.has(card.id)) continue;
    if (!card.isSite) continue;

    const toCell = host._cellAtWorld(m.mesh.position.x, m.mesh.position.z);
    if (!toCell) continue; // off-grid drops are handled elsewhere

    let blockingSite = null;
    for (const [, otherMesh] of meshesWithoutDrag) {
      const other = otherMesh?.userData?.cardInstance;
      if (!other?.isSite) continue;
      const otherCell = host._cellAtWorld(otherMesh.position.x, otherMesh.position.z);
      if (!otherCell) continue;
      if (otherCell.col === toCell.col && otherCell.row === toCell.row) {
        blockingSite = other;
        break;
      }
    }

    if (!blockingSite) continue;

    // The Tower of Babel: The Apex of Babel is the one site that MAY share
    // a cell. Onto your The Base of Babel's square it merges into the
    // Tower; anywhere else it is an ordinary site placement, so it falls
    // through to the usual occupancy rules below.
    if (card.name === 'The Apex of Babel'
        && blockingSite.name === 'The Base of Babel'
        && !!blockingSite.rotated === !!card.rotated) {
      continue;
    }

    // Rubble is replaceable. Placing a site onto Rubble banishes the
    // Rubble and the new site takes the cell (codex "Enter": "this can
    // happen if Rubble was replaced by a site"). The new site does not
    // count as having "entered" for the unit beneath it.
    if (blockingSite.cardId === 'sorcery-rubble') {
      host.sendCardToPile(blockingSite, 'Banished');
      host.logAction('{card} replaces Rubble at [{cell}]', { card: cardLogLabel(card, msg('Site')), cell: getCellNumber(toCell) });
      continue;
    }

    toast.message(
      t("{name} can't share a cell with {other}.", { name: card.name || t('Site'), other: blockingSite.name || t('another site') }),
    );

    freshPlayIds.delete(card.id);
    tableMembers.splice(i, 1);
    host.sendToHand(card);
  }
}

export function applyFreshPlayGating(host, tableMembers, freshPlayIds) {
  if (!freshPlayIds || freshPlayIds.size === 0) return;
  const reachMap = host.dragging?.reach || new Map();

  // v1.104. Engine gates are now ADVISORY. The play always commits.
  // Each check that fails emits a yellow log warning so the player
  // (and us watching their match) can see what the engine flagged.
  // Avatar tap shortcut + grant consumption still wire on the
  // happy path so the existing site-play UX (auto-tap avatar, etc.)
  // keeps working when the placement IS legal.
  for (let i = tableMembers.length - 1; i >= 0; i--) {
    const m = tableMembers[i];
    const card = m.mesh?.userData?.cardInstance;
    if (!card || !freshPlayIds.has(card.id)) continue;

    const can = host._canPlayFromHand(card);
    // S4. Every fresh-play gate is ADVISORY. The client no longer bounces an
    // illegal play back to hand (that mutated authoritative zone on the acting
    // client. The play-bounce divergence class). The server is the sole
    // legality authority: it validates the cast intent and rejects an illegal
    // one (no diff, so nothing renders on either client). Each gate that fails
    // logs a yellow advisory; the drag halo already paints the legal cells
    // pre-drop.
    if (!can.ok && can.reason === 'owner_locked') {
      host.addAdvisoryLog(can.message || msg("{card} can't be cast.", { card: card.name || msg('This spell') }), card);
      continue;
    }
    // Monument placement. A Monument is a non-carriable artifact; per codex
    // "Casting Artifacts" its only conjuring option is the surface of one of
    // YOUR sites. Advisory: a Monument dropped on a void cell or an enemy site
    // logs a warning; the server rejects the cast if it's actually illegal.
    if (isMonumentCard(host._getFullCard(card.cardId))) {
      const cell = host._cellAtWorld(m.mesh.position.x, m.mesh.position.z);
      const onOwnedSite = cell && ownedSiteCells(
        host._meshesExcludingStagedDrag(), host._cellAtWorld, !!card.rotated,
      ).has(`${cell.col},${cell.row}`);
      if (!onOwnedSite) {
        host.addAdvisoryLog('{card} must be conjured on one of your sites.', card, { card: card.name });
        continue;
      }
    }
    // Positional cast restriction ("Must be cast to your back row." The
    // Round Table; corner / outer-column cards). Advisory: the drag halo paints
    // the legal cells, and the server rejects an out-of-position cast.
    const castPos = castPositionFor(card.name);
    if (castPos) {
      const cell = host._cellAtWorld(m.mesh.position.x, m.mesh.position.z);
      const grid = getGameGrid(host.state.spawnConfig);
      if (!isCastPositionLegal(card.name, cell, grid, !!card.rotated)) {
        host.addAdvisoryLog('{card} must be cast to your {position}.', card, { card: card.name, position: msg(CAST_POSITION_LABEL[castPos] || castPos.replace(/_/g, ' ')) });
        continue;
      }
    }
    // Owner element restriction (an element-restricted spellcaster owner, e.g.
    // an Omphalos "Earth and Water Spellcaster", may only cast a held spell
    // requiring one of its elements). Advisory. The server enforces the real
    // gate on the cast intent.
    if (card._castableOnlyBy) {
      const ownerInst = host.meshes.get(card._castableOnlyBy)?.userData?.cardInstance;
      if (ownerInst && !ownerInst._thresholdWaiverThisTurn && !ownerInst._spellcasterNoThresholdThisTurn
          && !ownerElementAllowsCast(host._getFullCard(ownerInst.cardId), host._getFullCard(card.cardId))) {
        host.addAdvisoryLog('{card} can only be cast by {owner}, which cannot cast that element.', card, { card: card.name, owner: ownerInst.name || msg('its owner') });
        continue;
      }
    }
    // Owner-relative summon restriction (Omphalos "summoned here"). Advisory.
    // the server enforces the summon cell on the cast intent.
    const ownerCells = ownerSummonCellsFor(host, card);
    if (ownerCells) {
      const cell = host._cellAtWorld(m.mesh.position.x, m.mesh.position.z);
      if (!cell || !ownerCells.has(`${cell.col},${cell.row}`)) {
        const ownerName = host.meshes.get(card._castableOnlyBy)?.userData?.cardInstance?.name || 'its owner';
        const where = ownerSummonRestriction(ownerName) === 'here' ? msg('on its owner') : msg('in a legal cell');
        host.addAdvisoryLog('{card} must be summoned {where} ({owner}).', card, { card: card.name, where, owner: ownerName });
        continue;
      }
    }
    if (!can.ok) {
      host.addAdvisoryLog(can.message || msg('{card} may not be playable right now.', { card: card.name || msg('Card') }), card);
    }

    if (card.isSite) {
      const place = host._canPlaceSiteAt(card, m);
      if (place.ok) {
        m._siteGrantToConsume = !!place.consumeGrant;
        m._siteAutoTapAvatarId = place.autoTapAvatarId || null;
      } else {
        host.addAdvisoryLog(place.message || msg('{card} placement may not be legal.', { card: card.name || msg('Site') }), card);
      }
    }

    const entry = reachMap.get(card.id);
    if (entry?.kind === 'spawn') {
      const toCell = host._cellAtWorld(m.mesh.position.x, m.mesh.position.z);
      const legal = toCell && entry.cells.has(`${toCell.col},${toCell.row}`);
      if (!legal) {
        host.addAdvisoryLog('{card} may not be playable on this cell.', card, { card: card.name || msg('Card') });
      }
    }
  }
}

export function applyMovementGating(host, tableMembers, freshPlayIds) {
  if (!tableMembers || tableMembers.length === 0) return;
  // v1.37c. Never short-circuit on an empty reach map: a drag of
  // ONLY tapped Minions/Avatars yields no entries (those are skipped
  // in _computeDragReach so no halos render), and we still need the
  // tapped-branch below to fire so cell-cross drops snap back. Reach
  // lookups for non-tapped cards remain `undefined` and short-circuit
  // their own branch.
  const reachMap = host.dragging?.reach || new Map();
  for (const m of tableMembers) {
    const card = m.mesh?.userData?.cardInstance;
    if (!card || freshPlayIds?.has(card.id)) continue;
    // Sites / tokens / plain artifacts move freely. Only units are
    // step-rule-gated. Avatars are also Minions for the purposes
    // of region restrictions (handled inside reachableLocationsOneStep
    // via the Avatar type check), and Automatons are minions per
    // codex so they obey the step rule too.
    if (!countsAsUnit(card)) continue;

    // v1.37b. A tapped unit's cell-cross drop is likely illegal
    // (its move action is spent). Warn in the log; the drop still
    // commits server-side as a questionable manual reposition.
    // Tapped cards have no reach entry (skipped in
    // _computeDragReach) so they'd otherwise silently fall through.
    //
    // v1.54. Same gate applies to summoning sickness. A freshly
    // summoned minion can be nudged for stacking but can't take a
    // step (Move and Attack ability requires a cost-paying tap,
    // which sickness blocks).
    if (card.tapped || hasSummoningSickness(card)) {
      if (m.initialX == null || m.initialZ == null) continue;
      const fromCellT = host._cellAtWorld(m.initialX, m.initialZ);
      const toCellT   = host._cellAtWorld(m.mesh.position.x, m.mesh.position.z);
      if (!fromCellT || !toCellT) continue;
      if (fromCellT.col === toCellT.col && fromCellT.row === toCellT.row) continue;
      // v1.104. Advisory only, the move proceeds.
      const who = { card: card.name || msg('Card') };
      if (card.tapped) host.addAdvisoryLog('{card} has already moved this turn.', card, who);
      else host.addAdvisoryLog('{card} has summoning sickness.', card, who);
    }

    const entry = reachMap.get(card.id);
    if (!entry || entry.kind !== 'move') {
      // A PLACED unit with no reach entry (tapped / summoning-sick. And any
      // off-turn drag, since sick/tapped units get no halo either): still a
      // real drop. Emit the move intent and let the SERVER decide. An
      // off-turn drag commits as a manual reposition (manual-override
      // policy), an illegal on-turn drag is rejected (toast names it). Either
      // way the mesh glides to its server slot instead of floating where the
      // cursor let go.
      if (!card.isSite && card.type !== 'Aura'
        && m.initialX != null && m.initialZ != null) {
        const dropPos = { x: m.mesh.position.x, z: m.mesh.position.z };
        const toCell = host._cellAtWorld(dropPos.x, dropPos.z);
        const fromCell = card.cell || host._cellAtWorld(m.initialX, m.initialZ);
        const manual = !host._automationsEnabled();
        // Manual session: a same-cell nudge is a real reposition too. The
        // coords persist, so emit even when the cell didn't change.
        if (toCell && fromCell && (manual
          || toCell.col !== fromCell.col || toCell.row !== fromCell.row)) {
          host._emitMoveIntent?.(card, toCell, card.subsurface || 'surface', false, dropPos);
          host._beginPendingMove?.(card, toCell);
        }
        // Manual session / free placement: no layout slot to snap to. The
        // card rests where it was dropped and the confirming diff folds to
        // those same coords. Snapping here would glide it back to the
        // mirror's STALE slot for the whole intent round-trip.
        if (!manual && !host._freeCardPlacement?.()) {
          host._snapCardToServerCell?.(card.id, { glide: true });
        }
      }
      continue;
    }
    if (m.initialX == null || m.initialZ == null) continue;
    const toCell = host._cellAtWorld(m.mesh.position.x, m.mesh.position.z);
    if (!toCell) continue;
    const fromCell = entry.fromCell;
    // Same-cell drop. No move intent (nothing changed server-side), but the
    // mesh must still SNAP to its layout slot: no diff will arrive to refold
    // it, so without this the card just sits wherever the cursor let go.
    // Manual session: the nudge IS the change. Persist the coords instead.
    if (fromCell && fromCell.col === toCell.col && fromCell.row === toCell.row) {
      if (!host._automationsEnabled()) {
        const dropPos = { x: m.mesh.position.x, z: m.mesh.position.z };
        host._emitMoveIntent?.(card, toCell, card.subsurface || 'surface', false, dropPos);
      } else if (!host._freeCardPlacement?.()) {
        host._snapCardToServerCell?.(card.id, { glide: true });
      }
      // Free placement: the drop point IS the rest pose. The release funnel's
      // SET_POSITION persists it; snapping to the mirror's STALE pos here was
      // the release → bounce-back → glide-forward same-cell reposition bug.
      continue;
    }

    const dropRegion = resolveDropRegion(entry.fromRegion, toCell.col, toCell.row, entry.reach);
    if (dropRegion == null) {
      // S4. The server is the sole legality authority. The client no longer
      // snaps an out-of-reach / region-blocked drop back to its pickup cell (the
      // old subsurface snap-back mutated authoritative position on the acting
      // client, the actor-vs-opponent divergence class). It advisory-logs, emits
      // the move intent anyway (the server re-runs the reach engine against its
      // own board), and tracks the move as pending. If the server accepts,
      // the confirming diff commits the new cell; if it rejects, the
      // pending sweep re-folds the card to its server cell. No bounce, no
      // snap-back. Skips the optimistic tap / Move-and-Attack below: nothing is
      // applied for a move the client believes illegal until the server confirms.
      const who = { card: card.name || msg('Card') };
      if (!card.subsurface) host.addAdvisoryLog('{card} may not be able to move there. Out of range or region blocked.', card, who);
      else if (card.subsurface === 'underwater') host.addAdvisoryLog("{card} can't leave a body of water on the subsurface.", card, who);
      else host.addAdvisoryLog("{card} can't leave a body of land on the subsurface.", card, who);
      // The move intent still goes out with the requested drop point. The
      // server is the authority and may accept it, in which case the confirming
      // diff relocates the card and clears the pending entry.
      if (host._emitMoveIntent) {
        host._emitMoveIntent(card, toCell, 'surface', host._automationsEnabled(),
          { x: m.mesh.position.x, z: m.mesh.position.z });
      }
      host._beginPendingMove?.(card, toCell);
      // No pre-emptive snap-back: the server commits questionable moves under
      // the manual-override policy, so the common outcome is acceptance. A
      // snap here made every such drop visibly bounce before the confirming
      // diff re-placed it (GH-72). A genuine rejection still heals through
      // the pending-move sweep, which re-folds the card to its server cell.
      continue;
    }

    // Legal move. Sync card.subsurface with the resolved region.
    // v1.104. When dropRegion is null (engine flagged the move as
    // illegal but we let it through), leave subsurface untouched.
    // We don't know what region the unit is "in" so don't guess.
    if (dropRegion != null) {
      const newSubsurface = (dropRegion === 'surface' || dropRegion === 'void')
        ? null
        : dropRegion;
      if (card.subsurface !== newSubsurface) {
        if (newSubsurface == null && card.subsurface) {
          // Surfacing: route through _setSubsurface so its equipped-walk
          // fires each carried artifact's on_surface (Buried / Sunken
          // Treasure). _setSubsurface doesn't tap; the move-tap below
          // still applies once.
          host._setSubsurface(card, null);
        } else {
          card.subsurface = newSubsurface;
          host._broadcastCardSubsurface?.(card.id, card.subsurface);
          if (newSubsurface === 'underwater') host.logAction('{card} now underwater', { card: cardLogLabel(card, msg('Card')) });
          else if (newSubsurface) host.logAction('{card} now underground', { card: cardLogLabel(card, msg('Card')) });
        }
      }
    }
    // P5-cast-move: a legal unit cell-cross move. Emit a server-VALIDATED move
    // INTENT. The server re-runs the reach engine against its own board and
    // commits the authoritative SET_POSITION (+ subsurface + tap), closing the
    // blind-teleport hole (a tampered client can't assert an out-of-reach drop).
    // Flag the card so its redundant per-result SET_POSITION command (emitted
    // later via _presenceRelease → _emitPosition) is suppressed; the move intent
    // is the single position writer. The local tap / subsurface stay for
    // optimistic visuals (idempotent with the intent's commit). Off the auth
    // path this no-ops and the legacy position command runs unchanged.
    //
    // A drag of a card the local player does NOT control (an un-automated
    // push/lure resolved by the effect's controller, valkenhall-qa#107) is the
    // effect's forced movement, not the unit's move action: the server commits
    // it as a manual reposition and never taps, so neither autoTap nor the
    // optimistic tap below may fire for it (a local-only tap would diverge
    // from the authoritative card state until the next fold).
    const movesOwnCard = host.isCardOnLocalSide(card);
    if (host._emitMoveIntent) {
      host._emitMoveIntent(card, toCell, dropRegion,
        host._automationsEnabled() && movesOwnCard,
        { x: m.mesh.position.x, z: m.mesh.position.z });
    }
    // S4. Track the move as pending; the confirming diff clears it, and the
    // TTL sweep reverts to the server cell if the move is rejected.
    host._beginPendingMove?.(card, toCell);

    // v1.36. Auto-tap on commit. The unit has used its move action;
    // it can't act again this turn unless something untaps it. Only
    // reaches here for cell-crossing moves (same-cell drops continue
    // above) and for cards that passed the reach validator.
    // Capture combat eligibility BEFORE the move-tap: Move and Attack is a
    // single tap covering both, so the tap must not disqualify the attack.
    const combatState = host._canBeginCombat(card);
    const combatEligible = !!combatState?.canAttack;
    // This drag WAS the unit's Move and Attack ability: a combatant on
    // its controller's turn, untapped and able to act before the move.
    // canAttack == canMove now (0 power is not an attack gate), so the
    // move_or_attack prompt opens for powerless units too.
    const usedMoveAndAttack = !!combatState?.canMove;
    // broadcast:false. The move intent's autoTap is the single server-side
    // tap writer (movementService). A separate SET_TAPPED broadcast tapped
    // the mirror even when the server rejected the move, so the pending
    // sweep's revert could never untap the unit.
    if (host._automationsEnabled() && movesOwnCard) {
      host._applyTap(card, m.mesh, true, { source: 'move', log: false, broadcast: false });
    }

    // Move and Attack: after a qualifying move, ask whether the player
    // meant to attack. The prompt opens AUTOMATICALLY whenever the unit can
    // attack from its new square. No need to pre-select "Move & Attack"
    // from a menu. An explicit "Move" choice (radial menu) skips it.
    const preIntent = host._pendingCombatIntent;
    host._pendingCombatIntent = null;
    const justMove = preIntent?.cardId === card.id && preIntent.intent === 'move';
    if (combatEligible && !justMove) {
      const targets = host._combatTargetsFor(card, toCell, effectiveMovementBonus(card));
      if (targets.length) {
        host._combatMoveRevert = {
          cardId: card.id, originX: m.initialX, originZ: m.initialZ,
          fromRegion: entry.fromRegion, wasTappedByMove: host._automationsEnabled(),
        };
        host._openMoveOrAttackPrompt(card, toCell);
        return;
      }
    }
    // Codex Intercept: "When an enemy using its Move and Attack
    // finishes its movement and does not attack, any of your units
    // there may tap to intercept and fight that enemy." That covers
    // EVERY Move-and-Attack that ends without an attack. An explicit
    // "Move only" choice, a move with no legal target, and a 0-power
    // unit's move alike. The attack-instead paths return above (the
    // Move-or-Attack prompt's own branches re-offer intercept on
    // "Move only" and on attack-modal cancel).
    if (usedMoveAndAttack) { host._maybeOfferInterceptAfterMove(card, toCell); return; }
  }
}

export function handleContextMenu(host, event) {
  // Only suppress the native menu here. Opening the card/pile menu
  // moved to the right-button mouseup path (maybeOpenContextMenu):
  // macOS fires contextmenu on PRESS, which popped the menu at the
  // start of every right-drag camera pan. mouseup plus a movement
  // threshold decides click-vs-pan instead, on every platform.
  event.preventDefault();
}

function maybeOpenContextMenu(host, event) {
  // Spectators are read-only. The context menu only exposes
  // mutating actions (draw, search, shuffle, equip, activate, etc.)
  // so it's blocked entirely.
  if (host.props.isSpectating) return;

  // Clear stale transient pickup state so an aborted left-click
  // (e.g. mouseup that fell outside the window) can't keep the
  // pending flag set across this interaction.
  host._pendingPileClick = null;

  host.closeAllMenus();

  const hits = host.scene.raycastObjects(event, host.getInteractableMeshes());

  // Walk past any non-actionable nearest hit (e.g. a revealed opponent
  // hand card floating behind the playmat in the camera's line of
  // sight) and pick the first card / pile / token / dice / tracker
  // button along the ray. Without this, a single overlapping non-
  // actionable mesh silently swallows the right-click. The user
  // perceives this as "the context menu randomly doesn't open."
  let hit = null;
  for (const raycastHit of hits) {
    const candidate = host.findHitObject(raycastHit.object);
    const type = candidate?.userData?.type;
    if (type === 'card' || type === 'pile' || type === 'token'
        || type === 'dice' || type === 'trackerButton') {
      hit = candidate;
      break;
    }
  }
  if (!hit) return;

  // Integrated tracker button: right-click = decrement
  if (hit.userData.type === 'trackerButton' && hit.userData.action === 'integrated') {
    host._flashTrackerButton(hit);
    host.decrementTracker(hit.userData.player, hit.userData.trackerKey);
    return;
  }

  if (hit.userData.type === 'card') {
    const cardInstance = hit.userData.cardInstance;
    // Include enough info for the menu to decide whether to show
    // Group selected / Ungroup.
    const selectedIds = [...host.selectedCardIds];
    let selectionAlreadyGrouped = false;
    if (selectedIds.length >= 2) {
      const firstMesh = host.meshes.get(selectedIds[0]);
      const sharedGid = firstMesh?.userData?.cardInstance?.groupId || null;
      if (sharedGid) {
        selectionAlreadyGrouped = selectedIds.every((id) => {
          const m = host.meshes.get(id);
          return m?.userData?.cardInstance?.groupId === sharedGid;
        });
      }
    }
    host.setState({
      contextMenu: {
        x: event.clientX,
        y: event.clientY,
        type: 'card',
        cardInstance,
        mesh: hit,
        selectionSize: host.selectedCardIds.size,
        groupId: cardInstance.groupId || null,
        selectedIds,
        selectionAlreadyGrouped,
        // Use the same side-of-table classification as the outline
        // (isCardOnLocalSide) rather than isOwnedByLocalPlayer. The
        // latter's offline shortcut and the transient state windows
        // during match handshakes would desync the menu label from
        // the visible ring color. And after a "Take control" flip,
        // the label would stay frozen on the pre-flip answer.
        isLocallyOwned: host.isCardOnLocalSide(cardInstance),
        isMultiplayer: host.state.connectionStatus !== 'offline',
        // Display-only. Opening the modal will recompute the target
        // list. We only need to know whether the menu item should
        // even appear (locally owned AND there's something on the
        // local side to attach it to).
        canEquip: host.isCardOnLocalSide(cardInstance)
          && host.getValidEquipTargets(cardInstance).length > 0,
      },
    });
  } else if (hit.userData.type === 'pile') {
    const pile = hit.userData.pile;
    // All pile-menu actions (draw, shuffle, search, scry, draw to
    // hand) are private to the pile's owner. Drop the menu entirely
    // for opponent piles instead of opening it with no usable items.
    if (!host.isPileLocallyOwned(pile)) {
      host.setState({ contextMenu: null });
      return;
    }
    host.setState({
      contextMenu: {
        x: event.clientX,
        y: event.clientY,
        type: 'pile',
        pile,
      },
    });
  } else if (hit.userData.type === 'token') {
    host.setState({
      contextMenu: {
        x: event.clientX,
        y: event.clientY,
        type: 'token',
        tokenInstance: hit.userData.tokenInstance,
      },
    });
  } else if (hit.userData.type === 'dice') {
    host.setState({
      contextMenu: {
        x: event.clientX,
        y: event.clientY,
        type: 'dice',
        diceInstance: hit.userData.diceInstance,
      },
    });
  }
}
